import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Select, Skeleton, Space, Spin } from 'antd';
import { LightningIcon, TreeEvergreenIcon } from '@phosphor-icons/react';
import { useViewerStore } from '../../../../store/store';
import { resolveEffectiveAnnualUsageKwh } from '../../../../helpers/utilityMonthlyDailyToAnnual';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import type { SelectedDesignForPricing, SavedAIDesign } from '../../../../store/oneButtonStore';
import './oneButton-css/EditorDashboardShell.css';
import { addModulesToAllRoofsMaxFill, addModulesToRoofsByIds, removeGreyModulesAndSingleModuleRoofs, SetModuleGraph } from '../Three-JS-Viewer/Controllers/ModuleControl';
import { clearAllModuleMeshes, restoreModulesLayoutFromSnapshot } from '../Three-JS-Viewer/Controllers/DefaultLayoutControl';
import { captureLiveModulesLayout } from '../Three-JS-Viewer/Controllers/Common';
import designServices from '../../../../services/oneButtonServices/designServices';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import {
    mergeHoverEstimateIntoPriceQuote,
    mergeHoverEstimateIntoPriceQuoteOverrides,
} from '../../../../services/oneButtonServices/applyHoverEstimateConvert';
import { setPathname } from '../../../../helpers/storeHelpers';
import { useProductionCalculationReady, useRunAiBlockedStatus } from '../../../../hooks/calculationStateHook';
import { openSiteViewer3dFromDashboard, startDeferredSiteProductionCalculations, isSiteViewer3dBlocked, ensureProductionReadyForBackgroundWork } from '../../../../helpers/siteViewer3dAccess';
import {
    clearPreContractOpenCanvasFlag,
    consumePreContractLaunchAction,
    shouldOpenCanvasOnConfigureLoad,
} from '../../../../pre-contract/lib/preContractLaunch';
import { HideObjectType } from '../../../../@types/enums';
import { LAYER_OPTIONS } from '../../../../Constants';
import {
    applyScenarioLayout,
    buildAiModuleFillingInput,
    restoreAllTrees,
    runGhostScenariosAndSave,
} from '../../../../services/aiModuleFillingService';
import { SetDisplayShadePoints } from '../Three-JS-Viewer/Controllers/RefreshShade';
import opportunityInfoServices from '../../../../services/oneButtonServices/opportunityInfoService';
import { extractTitleSearchOwners } from '../../../../helpers/titleSearchOwners';
import WishListSection from './WishlistSection';
import { captureAIResults, loadBatchState, saveBatchState } from '../../../../services/batchAIService';
import {
    getSalesforceIdForAiCache,
    invalidateAiResultSessionCache,
    saveLocalAiResultCache,
    syncAiResultCacheTabRegistration,
} from '../../../../services/geminiService';
import { runPredictIdealLayoutAction } from './dashboardActionPredictIdealLayout';
import { runReRunTreeScenariosAction } from './dashboardActionReRunTreeScenarios';
import { runAiPreludeAction } from './dashboardActionRunAiPrelude';
import { runGenerateLayoutVariantsAction } from './dashboardActionGenerateLayoutVariants';
import { runGenerateCategoryLayoutVariantsAction } from './dashboardActionGenerateCategoryLayoutVariants';
import { runAiFinalizeAction } from './dashboardActionRunAiFinalize';
import { runUseThisLayoutAction } from './dashboardActionUseThisLayout';
import { runSimulateLayoutsForKeysAction } from './dashboardActionSimulateLayouts';
import { runPostAiLayoutPipelineAction } from './dashboardActionPostAiLayoutPipeline';
import { beginBatchLayoutSiteCalc, forceEndBatchLayoutSiteCalc } from './utils/batchLayoutSiteCalc';
import { clearPendingUnghostAlert, beginTreeScenarioLayoutSuppress, endTreeScenarioLayoutSuppress } from '../Three-JS-Viewer/Controllers/TreeImpactEligibility';
import {
    collectCategoryCandidates,
    candidateLayoutSignature,
    formatCategoryWinnerDisplayLabel,
    getCategoryMainLayoutKey,
    getLayoutCategoryNames,
    rankCategoryCandidates,
    resolveCategoryCardDisplayLabel,
    resolveCategoryMainCardCandidate,
    normalizeLayoutMinEff,
} from './utils/categoryBestVariantScoring';
import {
    logAiDesignOpenStart,
    logAiDesignResolveInputs,
    logAiDesignOpenEnd,
    verifyAppliedDesign,
    syncActiveAiDesignProductionFromCard,
} from './utils/aiDesignFlowDebug';
import { applyCategoryLayoutInViewer, isSnapshotApplicableToViewer, resolveApplyCategoryLabel } from './utils/applyCategoryLayoutInViewer';
import { parseRoofCombinationsFromAi } from './utils/parseRoofCombinationsFromAi';
import { deriveRoofCombinationsFromLayoutCatalog, resolveRoofCombinationsForRunAi } from './utils/resolveRoofCombinationsForRunAi';
import { resolveRoofIdsForLayoutKey } from './utils/resolveRoofIdsForLayoutKey';
import {
    applyAiScenariosCacheToViewerStore,
    buildAiScenariosCacheBlobForLocalPersist,
    buildCachedMainLayoutCandidate,
    hasHydratedAiLayoutCatalogInStore,
    hasSimulatedLayoutCatalogData,
    hasUsableAiDesignCatalogInStore,
    tryReapplyCategoryBestLayoutsFromStore,
    clearWishlistCacheForRun,
} from './utils/aiLayoutCacheHydration';
import { clearAiLayoutSnapshotStateForRun } from './utils/catalogCardSnapshot';
import { resolveVariantsForCategory } from './utils/filterAltLayoutDisplayVariants';
import { parseAiSummaryBlocks } from './parseAiSummaryBlocks';
import type { EditorDashboardProps } from './editorDashboardTypes';
import { countSiteTrees, isLayoutProductionOverAnnualUsage, resolveLayoutRoofCount } from './utils/layout';
import {
    TREE_CUT_PRICING_PARTNER_COLUMNS,
    annualKwhEstimateForTreeCutFinancingModal,
    type MainCardPartnerCellState,
    type TreeCutPartnerCellState,
    type TreeCutPricingPartnerColumn,
    type TreeCutPricingTable,
} from './utils/pricing';
import { useDashboardVisibility } from './hooks/useDashboardVisibility';
import { useOpportunityInitialization } from './hooks/useOpportunityInitialization';
import { useSaveAiScenarios } from './hooks/useDashboardActions';
import { useDashboardStoreBindings } from './hooks/useDashboardStoreBindings';
import { useDashboardDesignHandlers } from './hooks/useDashboardDesignHandlers';
import { useTreeCutModalState } from './hooks/useTreeCutModalState';
import { useTreeCutScenarioPricing } from './hooks/useTreeCutScenarioPricing';
import { useMainCardScenarioPricing } from './hooks/useMainCardScenarioPricing';
import { DashboardOverlayExtrasSection } from './sections/DashboardOverlayExtrasSection';
import { DashboardLoadingGate } from './sections/DashboardLoadingGate';
import { DashboardReadyLayoutSection } from './sections/DashboardReadyLayoutSection';
import { DashboardSurfaceSection } from './sections/DashboardSurfaceSection';
import { DashboardOverlayShell } from './sections/DashboardOverlayShell';
import { OpportunityInitializationOverlay } from './sections/OpportunityInitializationOverlay';
import { DashboardAiRailPeekButton } from './sections/DashboardAiRailPeekButton';
import { isHoverJobAvailable } from '../../../../services/oneButtonServices/hoverServices';
import { SiteViewerUnavailableModal } from './sections/SiteViewerUnavailableModal';
import { DashboardAgentFooter } from '.';
import { DashboardSavedDesignsSection } from './sections/DashboardSavedDesignsSection';
import { DashboardAiRailAiEmptyState } from './sections/DashboardAiRailAiEmptyState';
import { DashboardRunAiBlockedStatus } from './sections/DashboardRunAiBlockedStatus';
import {
    DashboardAiDesignsSourceBadge,
    type AiDesignsDataSource,
} from './sections/DashboardAiDesignsSourceBadge';
import { DashboardMaxPanelsTreeCuttingModal } from './sections/DashboardMaxPanelsTreeCuttingModal';
import { DashboardAiRailSection } from './sections/DashboardAiRailSection';
import { DashboardTreeCuttingConfirmModal } from './sections/DashboardTreeCuttingConfirmModal';
import { DashboardPredictIdealLayoutModal } from './sections/DashboardPredictIdealLayoutModal';
import { DashboardAllAiDesignCombinationsModal } from './sections/DashboardAllAiDesignCombinationsModal';
import { DashboardAltLayoutOptionsModal } from './sections/DashboardAltLayoutOptionsModal';
import DashboardModal from './DashboardModal';
import { DashboardGeminiRoofSuggestionsModal } from './sections/DashboardGeminiRoofSuggestionsModal';
import { OBD_EDITOR_TREE_CUTTING_MODAL_Z } from './oneButtonOverlayZ';
import { DashboardAiScenariosModuleBar } from './sections/DashboardAiScenariosModuleBar';
import { DashboardSavingProgressToast } from './sections/DashboardSavingProgressToast';
import { DashboardAiRunPayloadForm } from './sections/DashboardAiRunPayloadForm';
import {
    applyAiRunScenarioSelection,
    getDefaultAiRunPayloadSelection,
    isAiPayloadSizeExceededError,
    isAiRunPayloadSelectionContinueDisabled,
    type AiRunPayloadSelection,
} from './aiRunPayloadSelection';
import { DashboardAiScenariosHeader } from './sections/DashboardAiScenariosHeader';
import { DashboardAiRailTreeCuttingPanel } from './sections/DashboardAiRailTreeCuttingPanel';
import { DashboardAiLayoutCardView } from './sections/DashboardAiLayoutCardView';
import { DashboardAiLayoutTableView } from './sections/DashboardAiLayoutTableView';
import { DashboardAiSlideDesignsList } from './sections/DashboardAiSlideDesignsList';
import { DashboardAiSlideWishlistList } from './sections/DashboardAiSlideWishlistList';
import { DashboardAiDesignsTabChrome } from './sections/DashboardAiDesignsTabChrome';
import type { DashboardSectionPanelId } from './dashboardSectionNav';
import { STABLE_EMPTY_ARRAY, STABLE_EMPTY_OBJECT } from './utils/stableEmpty';
import { SHADE_CALCULATIONS, siteCalculationState } from '../../../../services/siteCalculationState';
import { useCurrentUser } from '../../../../contexts/UserContext';
export { EditorDashboardToggle } from './EditorDashboardToggle';

export const EditorDashboard: React.FC<EditorDashboardProps> = (props) => {
    const navigate = useNavigate();
    const {
        // state / toggles
        isDashboardOpen: isDashboardOpenProp,

        // AI-related data
        // callbacks
        setIsDashboardOpen: setIsDashboardOpenProp,
        handleReRunTreeScenarios: handleReRunTreeScenariosProp,
        handleSimulateLayoutsForKeys: handleSimulateLayoutsForKeysProp,
        handleUseThisLayout: handleUseThisLayoutProp,
        idealLayoutRecommendation: idealLayoutRecommendationProp,
        idealLayoutLoading: idealLayoutLoadingProp,
        handlePredictIdealLayout: handlePredictIdealLayoutProp,
        best5MaxPanelsTreeCuttingDesigns: best5MaxPanelsTreeCuttingDesignsProp = [],
        treeCount: treeCountProp = 0,
        sceneViewer: sceneViewerProp,
        // Optional: external AI rail state (for viewer-only rail)
        aiRailOpen: aiRailOpenProp,
        setAiRailOpen: setAiRailOpenProp,
    } = props;

    const { user, hasCapability } = useCurrentUser();
    const { isDashboardOpen, setIsDashboardOpen } = useDashboardVisibility({
        isDashboardOpenProp,
        setIsDashboardOpenProp,
    });

    const { savingScenarios, aiSavingProgress, handleSaveAiScenarios } = useSaveAiScenarios({
        sceneViewerProp,
    });

    const isBatchMode = useMemo(() => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('batch') === 'true';
    }, []);
    const [batchAiTriggered, setBatchAiTriggered] = useState(false);
    const [batchResultCaptured, setBatchResultCaptured] = useState(false);
    const batchRunAiFallbackRef = useRef<(() => void) | null>(null);
    const lastAutoHydrateSavedAiDesignsSfRef = useRef<string | null>(null);
    const {
        loadingCompleted,
        refreshingShadeFromStore,
        roofInfoArr,
        designListFromStore,
        siteDesignDetailsFromStore,
        setAiLoadingStep,
        setLayoutModulesByKey,
        setLayoutProductionByKey,
        setLayoutMetricsByKey,
        setAiScenariosSectionVisible,
        setAiLoading,
        setLayoutSimulationTimeByKey,
        handleLoadSavedDesigns,
    } = useDashboardStoreBindings();
    const setAiResult = useCallback((next: string | null) => {
        useViewerStore.setState({ aiResult: next } as any);
    }, []);
    const setAiRunStartMs = useCallback((next: number | null) => {
        useViewerStore.setState({ aiRunStartMs: next } as any);
    }, []);
    const setAiRunElapsedMs = useCallback((next: number | null) => {
        useViewerStore.setState({ aiRunElapsedMs: next } as any);
    }, []);
    const setSimulatingLayouts = useCallback((next: boolean) => {
        useViewerStore.setState({ simulatingLayouts: next } as any);
    }, []);
    const setSimulatingLayoutIndex = useCallback((next: number) => {
        useViewerStore.setState({ simulatingLayoutIndex: next } as any);
    }, []);
    const setSimulatingLayoutTotal = useCallback((next: number) => {
        useViewerStore.setState({ simulatingLayoutTotal: next } as any);
    }, []);
    const handleReRunTreeScenariosPropOrUndefined =
        typeof handleReRunTreeScenariosProp === 'function'
            ? handleReRunTreeScenariosProp
            : undefined;
    const handleSimulateLayoutsForKeysPropOrUndefined =
        typeof handleSimulateLayoutsForKeysProp === 'function'
            ? handleSimulateLayoutsForKeysProp
            : undefined;
    const handleUseThisLayoutPropOrUndefined =
        typeof handleUseThisLayoutProp === 'function'
            ? handleUseThisLayoutProp
            : undefined;
    const setIdealLayoutRecommendation = useCallback((next: string | null) => {
        useViewerStore.setState({ idealLayoutRecommendation: next } as any);
    }, []);
    const setIdealLayoutLoading = useCallback((next: boolean) => {
        useViewerStore.setState({ idealLayoutLoading: next } as any);
    }, []);
    const handlePredictIdealLayoutLocal = useCallback(async () => {
        const state = useViewerStore.getState() as any;
        const aiText = (typeof state.aiResult === 'string' ? state.aiResult : '').trim();
        const base = (state.aiModuleFillingScenarios || [])?.[0];
        const weights = base?.shadeWeights ?? [];
        const validIds = new Set((weights || []).map((r: any) => r.roofFaceId).filter(Boolean));
        const combos: Array<{ name: string; roofFaceIds: string[] }> = [];
        const match = aiText.match(/RECOMMENDED_ROOF_COMBINATIONS:\s*([^\n]+)/i);
        if (match) {
            match[1].split(';').forEach((part: string) => {
                const trimmed = part.trim();
                if (!trimmed) return;
                const pipeIdx = trimmed.indexOf('|');
                const name = pipeIdx >= 0 ? trimmed.slice(0, pipeIdx).trim() : trimmed;
                const idsStr = pipeIdx >= 0 ? trimmed.slice(pipeIdx + 1) : '';
                const roofFaceIds = idsStr
                    .split(',')
                    .map((s: string) => s.trim())
                    .filter((id: string) => validIds.has(id));
                if (name && roofFaceIds.length > 0) combos.push({ name, roofFaceIds });
            });
        }
        await runPredictIdealLayoutAction({
            layoutProductionByKey: (state.layoutProductionByKey || {}) as Record<string, number>,
            layoutModulesByKey: (state.layoutModulesByKey || {}) as Record<string, number>,
            roofCombinationsFromAi: combos,
            setIdealLayoutRecommendation,
            setIdealLayoutLoading,
        });
    }, [setIdealLayoutRecommendation, setIdealLayoutLoading]);
    const handlePredictIdealLayout =
        typeof handlePredictIdealLayoutProp === 'function'
            ? handlePredictIdealLayoutProp
            : handlePredictIdealLayoutLocal;
    const [initialDashboardReady, setInitialDashboardReady] = useState(false);
    const [oppRefreshKey, setOppRefreshKey] = useState(0);
    const [opportunityAssistantOpen, setOpportunityAssistantOpen] = useState(false);
    const salesForceId = useOneButtonStore((s) => s.salesForceId);
    const isOpportunityInitializing = useOneButtonStore((s) => s.isOpportunityInitializing);
    const opportunityInitializedSfid = useOneButtonStore((s) => s.opportunityInitializedSfid);
    const opportunityInitializationError = useOneButtonStore((s) => s.opportunityInitializationError);
    const opportunityInitManagedByOrchestrator = useOneButtonStore(
        (s) => s.opportunityInitManagedByOrchestrator,
    );
    const sceneViewerFromStore = useViewerStore((s) => s.sceneViewer);
    const sceneViewer = sceneViewerProp ?? sceneViewerFromStore;
    const aiLoading = useViewerStore((s: any) => Boolean(s.aiLoading));
    const aiResult = useViewerStore((s: any) => (typeof s.aiResult === 'string' ? s.aiResult : null));
    const aiError = useViewerStore((s: any) => (typeof s.aiError === 'string' ? s.aiError : null));
    const idealLayoutRecommendationFromStore = useViewerStore((s: any) => (typeof s.idealLayoutRecommendation === 'string' ? s.idealLayoutRecommendation : null));
    const idealLayoutLoadingFromStore = useViewerStore((s: any) => Boolean(s.idealLayoutLoading));
    const idealLayoutRecommendation =
        typeof idealLayoutRecommendationProp === 'string'
            ? idealLayoutRecommendationProp
            : idealLayoutRecommendationFromStore;
    const idealLayoutLoading =
        typeof idealLayoutLoadingProp === 'boolean'
            ? idealLayoutLoadingProp
            : idealLayoutLoadingFromStore;
    const selectedRoofCombinationKey = useViewerStore((s: any) => s.selectedRoofCombinationKey ?? 'default');
    const siteAddress = useViewerStore((s) => s.siteAddress);
    const siteLoadingError = useViewerStore((s) => s.siteLoadingError);
    const siteDesigns = useViewerStore((s) => s.siteDesigns);
    const siteViewerUnavailable = useMemo(
        () =>
            Boolean(
                siteLoadingError?.trim() ||
                    (loadingCompleted && !siteDesigns?.aurora && !siteDesigns?.eagleView),
            ),
        [siteLoadingError, loadingCompleted, siteDesigns?.aurora, siteDesigns?.eagleView],
    );
    const sfAnnualUsage = useViewerStore((s) => s.SFannualUsage);
    const oneButtonAnnualUsageOverride = useViewerStore((s) => s.annualUsage);
    const solarUtilityDataForAnnual = useOneButtonStore((s) => s.solarUtilityData);
    const useOneButtonAnnualInput = hasCapability('Show_OneButton');
    const dashboardAnnualUsage = useMemo(() => {
        const su = solarUtilityDataForAnnual?.SolarUtility as Record<string, unknown> | undefined;
        return resolveEffectiveAnnualUsageKwh({
            useOneButtonAnnualInput,
            oneButtonAnnualUsageOverride,
            solarUtility: su,
            sfAnnualUsage,
        });
    }, [solarUtilityDataForAnnual, sfAnnualUsage, oneButtonAnnualUsageOverride, useOneButtonAnnualInput]);
    const dashboardSystemSizeKw = useViewerStore((s) => s.totalSystemSize) ?? null;
    const dashboardMultiplierRaw = useViewerStore((s) => s.multiplier) ?? sessionStorage.getItem('Multiplier');
    const dashboardMultiplier =
        dashboardMultiplierRaw && !Number.isNaN(Number(dashboardMultiplierRaw))
            ? Number(dashboardMultiplierRaw)
            : null;
    const dashboardAvgDailyUsage =
        dashboardAnnualUsage != null && dashboardAnnualUsage > 0
            ? Math.round(dashboardAnnualUsage / 365)
            : null;
    const layoutProductionByKey = useViewerStore(
        (s: any) => (s.layoutProductionByKey ?? STABLE_EMPTY_OBJECT) as Record<string, number>,
    );
    const layoutModulesByKey = useViewerStore(
        (s: any) => (s.layoutModulesByKey ?? STABLE_EMPTY_OBJECT) as Record<string, number>,
    );
    const layoutMetricsByKey = useViewerStore(
        (s: any) =>
            (s.layoutMetricsByKey ?? STABLE_EMPTY_OBJECT) as Record<
                string,
                {
                    efficiency: number | null;
                    solarAccess: number | null;
                    treeCount: number | null;
                    roofCount: number | null;
                    multiplier?: number | null;
                }
            >,
    );
    const layoutSimulationTimeByKey = useViewerStore(
        (s: any) => (s.layoutSimulationTimeByKey ?? STABLE_EMPTY_OBJECT) as Record<string, number>,
    );
    const layoutModulesLayoutByKey = useViewerStore(
        (s: any) =>
            (s.layoutModulesLayoutByKey ?? STABLE_EMPTY_OBJECT) as Record<
                string,
                import('../../../../@types/types').SavedRoofModulesLayout | undefined
            >,
    );
    const categoryMainLayoutByCategory = useViewerStore(
        (s: any) =>
            (s.categoryMainLayoutByCategory ?? STABLE_EMPTY_OBJECT) as Record<
                string,
                import('./utils/categoryBestVariantScoring').CategoryLayoutCandidate
            >,
    );

    const aiResultText = (aiResult ?? '') || '';
    const aiModuleFillingScenarios = useViewerStore((s: any) => (s.aiModuleFillingScenarios || []) as any[]);
    const [aiPayloadRetrySelection, setAiPayloadRetrySelection] = useState<AiRunPayloadSelection>(() =>
        getDefaultAiRunPayloadSelection(0),
    );
    const handleAiPayloadRetrySelectionChange = useCallback((sel: AiRunPayloadSelection) => {
        setAiPayloadRetrySelection(sel);
    }, []);
    const applyingScenarioIndex = useViewerStore((s: any) => (s as any).applyingScenarioIndex ?? null);
    const setApplyingScenarioIndex = useCallback((next: number | null) => {
        useViewerStore.setState({ applyingScenarioIndex: next } as any);
    }, []);
    const [applyingTreeLayoutFromScenario, setApplyingTreeLayoutFromScenario] = useState(false);

    const baseMultiplier = aiModuleFillingScenarios?.[0]?.systemMultiplier ?? 0;
    const baseWattage = aiModuleFillingScenarios?.[0]?.actualWattage ?? 0;
    const totalModulesBase = useMemo(() => {
        const base = aiModuleFillingScenarios?.[0];
        const weights = base?.shadeWeights ?? [];
        return weights.reduce((sum: number, r: any) => sum + (r.moduleCount ?? 0), 0);
    }, [aiModuleFillingScenarios]);

    const TREE_GAIN_MIN_PCT_LOCAL = 0.1;
    const TREE_GAIN_MIN_PER_1K_LOCAL = 0.05;
    const singleTreeWithWorthIt = useMemo(() => {
        const scenarios = aiModuleFillingScenarios ?? [];
        if (!baseWattage || baseWattage <= 0) {
            return scenarios.map((s: any) => ({ ...s, gainPct: 0, costNum: s.cutCost != null ? Number(s.cutCost) : NaN, worthIt: false }));
        }
        return scenarios.map((s: any) => {
            const isMulti = (s.treeIds?.length ?? 0) > 1;
            const gainPct = (s.treeId != null || isMulti) && baseWattage ? ((s.actualWattage ?? 0) - baseWattage) / baseWattage * 100 : 0;
            const costNum = s.cutCost != null ? Number(s.cutCost) : NaN;
            const worthIt =
                (s.treeId != null || isMulti) &&
                gainPct >= TREE_GAIN_MIN_PCT_LOCAL &&
                (costNum === 0 || !Number.isFinite(costNum) || gainPct / (costNum / 1000) >= TREE_GAIN_MIN_PER_1K_LOCAL);
            return { ...s, gainPct, costNum, worthIt };
        });
    }, [aiModuleFillingScenarios, baseWattage]);

    const multiTreeScenarios = useMemo(() => {
        const scenarios = aiModuleFillingScenarios ?? [];
        return scenarios
            .map((s: any, index: number) => ({ ...s, index }))
            .filter((s: any) => s.treeIds && s.treeIds.length > 1);
    }, [aiModuleFillingScenarios]);

    const aiResultSections = useMemo(() => {
        const text = (aiResultText || '').trim();
        if (!text) return { bestRoofs: '', treeCutting: '', summary: '' };
        const parts = text.split(/\n\s*\d+\.\s+/);
        const bestRoofs = [(parts[1] ?? '').trim(), (parts[2] ?? '').trim()].filter(Boolean).join('\n\n');
        return {
            bestRoofs,
            treeCutting: (parts[3] ?? '').trim(),
            summary: (parts[4] ?? '').trim(),
        };
    }, [aiResultText]);

    const formattedSummaryBlocks = useMemo(() => parseAiSummaryBlocks(aiResultText || null), [aiResultText]);

    /** Roof list + shade from the AI base scenario only (used to parse Gemini roof combinations; stable across layout selection). */
    const roofCardsFromAiBase = useMemo(() => {
        const base = aiModuleFillingScenarios?.[0];
        const weights = base?.shadeWeights ?? [];
        const totalWattage = typeof base?.actualWattage === 'number' ? base.actualWattage : 0;
        const sumMult = weights.reduce((s: number, r: any) => s + (r.roofMultiplier ?? 0), 0);
        const sumModuleWeight = weights.reduce((s: number, r: any) => s + (r.moduleCount ?? 0) * (r.roofMultiplier ?? 0), 0);
        const cards = weights.map((r: any) => {
            const pointCount = r.shadePoints?.length ?? 0;
            const avgPtHash = pointCount && r.shadePoints
                ? r.shadePoints.reduce((a: number, p: any) => a + (p.ptHashVal ?? 0), 0) / pointCount
                : r.solarAccess ?? 0;
            const moduleCount = r.moduleCount ?? 0;
            const shareFormula = sumModuleWeight > 0 && totalWattage
                ? ((moduleCount * (r.roofMultiplier ?? 0)) / sumModuleWeight) * totalWattage
                : sumMult > 0 ? ((r.roofMultiplier ?? 0) / sumMult) * totalWattage : 0;
            const hasActual = r.actualProduction != null && Number.isFinite(r.actualProduction);
            const productionValue = hasActual ? r.actualProduction : shareFormula;
            return {
                roofFaceId: r.roofFaceId,
                roofNum: r.roofNum,
                solarAccess: r.solarAccess ?? 0,
                roofMultiplier: r.roofMultiplier ?? 0,
                moduleCount,
                pointCount,
                avgPtHash: Math.round(avgPtHash),
                productionApprox: Math.round(productionValue),
                productionIsExact: hasActual,
            };
        });
        cards.sort((a: any, b: any) => (b.productionApprox ?? 0) - (a.productionApprox ?? 0));
        return cards;
    }, [aiModuleFillingScenarios]);

    const layoutRoofSummariesByKey = useViewerStore(
        (s: any) => (s.layoutRoofSummariesByKey ?? STABLE_EMPTY_OBJECT) as Record<string, any[]>,
    );

    /** Per-roof shading for the selected design after layout simulation; falls back to AI base scenario. */
    const roofCards = useMemo(() => {
        const fromSim = layoutRoofSummariesByKey[selectedRoofCombinationKey || 'default'];
        if (Array.isArray(fromSim) && fromSim.length > 0) {
            return fromSim;
        }
        return roofCardsFromAiBase;
    }, [layoutRoofSummariesByKey, selectedRoofCombinationKey, roofCardsFromAiBase]);

    const aiRecommendedRoofIds = useMemo(() => {
        const text = (aiResultText || '').trim();
        if (!text) return [];
        const match = text.match(/RECOMMENDED_ROOF_IDS:\s*([^\n]+)/i);
        if (!match) return [];
        return match[1].split(',').map((s: string) => s.trim()).filter(Boolean);
    }, [aiResultText]);

    const aiResolvedRoofCombinations = useViewerStore(
        (s: any) => (s.aiResolvedRoofCombinations || []) as Array<{ name: string; roofFaceIds: string[] }>,
    );

    const roofCombinationsFromAi = useMemo(() => {
        if (Array.isArray(aiResolvedRoofCombinations) && aiResolvedRoofCombinations.length > 0) {
            return aiResolvedRoofCombinations;
        }
        const validIds = new Set<string>(
            (roofCardsFromAiBase || [])
                .map((r: any) => r.roofFaceId)
                .filter((id: unknown): id is string => typeof id === 'string'),
        );
        if (validIds.size === 0 && sceneViewer?.roofMeshArr) {
            for (const mesh of sceneViewer.roofMeshArr as Array<{ roofFaceId?: string }>) {
                if (typeof mesh.roofFaceId === 'string' && mesh.roofFaceId) {
                    validIds.add(mesh.roofFaceId);
                }
            }
        }
        const weights = aiModuleFillingScenarios?.[0]?.shadeWeights ?? [];
        const fromAi = resolveRoofCombinationsForRunAi(aiResultText, validIds, weights);
        if (fromAi.length > 0) return fromAi;
        return deriveRoofCombinationsFromLayoutCatalog({
            categoryMainLayoutByCategory,
            layoutModulesLayoutByKey,
        });
    }, [
        aiResultText,
        roofCardsFromAiBase,
        aiResolvedRoofCombinations,
        aiModuleFillingScenarios,
        categoryMainLayoutByCategory,
        layoutModulesLayoutByKey,
        sceneViewer,
    ]);

    const bestRoofsForLayout = useMemo(() => {
        const cards = Array.isArray(roofCards) ? roofCards : [];
        const validIds = new Set(cards.map((r: any) => r.roofFaceId).filter(Boolean));
        const fromAi = (aiRecommendedRoofIds || []).filter((id: string) => validIds.has(id));
        if (fromAi.length > 0) {
            const list = cards.filter((r: any) => fromAi.includes(r.roofFaceId));
            const totalProduction = list.reduce((s: number, r: any) => s + (r.productionApprox ?? 0), 0);
            return { roofFaceIds: fromAi, totalEstimatedProduction: Math.round(totalProduction), fromAi: true };
        }
        const MIN_SOLAR = 50;
        const good = cards.filter((r: any) => (r.solarAccess ?? 0) >= MIN_SOLAR || (r.avgPtHash ?? 0) >= MIN_SOLAR);
        const list = good.length > 0 ? good : cards.slice(0, 3);
        const totalProduction = list.reduce((s: number, r: any) => s + (r.productionApprox ?? 0), 0);
        return { roofFaceIds: list.map((r: any) => r.roofFaceId).filter(Boolean), totalEstimatedProduction: Math.round(totalProduction), fromAi: false };
    }, [roofCards, aiRecommendedRoofIds]);

    const defaultMaxfillRoofIds = useMemo(() => {
        if (!(sceneViewer as any)?.roofMeshArr?.length) return [];
        return (sceneViewer as any).roofMeshArr.map((r: any) => r.roofFaceId).filter(Boolean);
    }, [sceneViewer]);

    const activeLayoutRoofIds = useMemo(() => {
        if (selectedRoofCombinationKey === 'default') return defaultMaxfillRoofIds;
        const combo = (roofCombinationsFromAi || []).find((c: any) => c.name === selectedRoofCombinationKey);
        return combo?.roofFaceIds ?? defaultMaxfillRoofIds;
    }, [selectedRoofCombinationKey, defaultMaxfillRoofIds, roofCombinationsFromAi]);


    const expectedCountByRoofId = useMemo(() => {
        const map: Record<string, number> = {};
        const self = (sceneViewerProp ?? (useViewerStore.getState() as any).sceneViewer) as any;
        if (!self || activeLayoutRoofIds.length === 0) return map;
        activeLayoutRoofIds.forEach((id: string) => {
            // `getExpectedModuleCountForRoof` is available on viewer ModuleControl via sceneViewer methods in this codebase.
            map[id] = (self as any)?.getExpectedModuleCountForRoof?.(id) ?? 0;
        });
        return map;
    }, [sceneViewerProp, activeLayoutRoofIds]);

    const modeCountForLayout = useViewerStore((s: any) => (typeof s.modeCount === 'number' ? s.modeCount : 0));
    const actualWattageForLayout = useViewerStore((s: any) => (typeof s.actualWattage === 'number' ? s.actualWattage : 0));
    const totalModulesBestRoofs = useMemo(() => {
        const key = selectedRoofCombinationKey || 'default';
        const fromSim = layoutModulesByKey[key];
        if (typeof fromSim === 'number' && Number.isFinite(fromSim)) return fromSim;
        if (key === 'default') return modeCountForLayout;
        return 0;
    }, [layoutModulesByKey, selectedRoofCombinationKey, modeCountForLayout]);

    const activeLayoutProduction = useMemo(() => {
        const key = selectedRoofCombinationKey || 'default';
        const fromSim = layoutProductionByKey[key];
        if (typeof fromSim === 'number' && Number.isFinite(fromSim)) return fromSim;
        const idsSet = new Set(activeLayoutRoofIds);
        if (idsSet.size > 0 && roofCards.length > 0) {
            const sum = roofCards
                .filter((r: any) => r.roofFaceId && idsSet.has(r.roofFaceId))
                .reduce((s: number, r: any) => s + (r.productionApprox ?? 0), 0);
            if (Number.isFinite(sum) && sum > 0) return sum;
        }
        return actualWattageForLayout;
    }, [layoutProductionByKey, selectedRoofCombinationKey, activeLayoutRoofIds, roofCards, actualWattageForLayout]);
    const treeArr = useViewerStore((s: any) => (s.treeArr || []) as any[]);
    const best5MaxPanelsTreeCuttingDesigns = useMemo(() => {
        if (Array.isArray(best5MaxPanelsTreeCuttingDesignsProp) && best5MaxPanelsTreeCuttingDesignsProp.length > 0) {
            return best5MaxPanelsTreeCuttingDesignsProp;
        }
        const scenarios = (aiModuleFillingScenarios || []) as any[];
        if (!scenarios.length) return [];
        const baseW = scenarios[0]?.actualWattage ?? 0;
        const withIndex = scenarios.map((s, index) => {
            const weights = s.shadeWeights ?? [];
            const modules = weights.reduce((sum: number, r: any) => sum + (r.moduleCount ?? 0), 0);
            const roofs = weights.length;
            return {
                scenarioIndex: index,
                actualWattage: s.actualWattage ?? 0,
                cutCost: s.cutCost != null ? Number(s.cutCost) : 0,
                treeLabel: s.treeId != null ? `Cut tree ${s.treeLabel ?? s.treeId}` : (s.treeIds?.length ? `Cut trees ${(s.treeIds as string[]).join(', ')}` : 'No tree cuts'),
                isBase: s.treeId == null && !(s.treeIds?.length),
                modules,
                roofs,
            };
        });
        const scored = withIndex.map((d) => {
            const gain = d.actualWattage - baseW;
            const costK = d.cutCost / 1000;
            const score = costK <= 0 ? d.actualWattage : d.actualWattage - costK * 40;
            return { ...d, gain, score };
        });
        scored.sort((a, b) => b.score - a.score);
        return scored.slice(0, 5);
    }, [best5MaxPanelsTreeCuttingDesignsProp, aiModuleFillingScenarios]);
    const treeCount = useMemo(
        () =>
            countSiteTrees({
                sceneViewer,
                treeArr,
                treeCountProp,
                layoutMetricsByKey: layoutMetricsByKey as Record<string, { treeCount?: number | null }>,
            }),
        [treeCountProp, sceneViewer, treeArr, layoutMetricsByKey],
    );
    const simulatingLayouts = useViewerStore((s: any) => Boolean(s.simulatingLayouts));
    const isLoadingSnapshots = useViewerStore((s: any) => Boolean((s as any).isLoadingSnapshots));
    // Viewer-level overlays (AI run + simulating layouts) are rendered in `ThreeDViewer`.
    const treeReprocessInProgress = useViewerStore((s: any) => Boolean((s as any).treeReprocessInProgress));
    const resultCardMinimized = useViewerStore((s: any) => Boolean(s.resultCardMinimized));
    const setResultCardMinimized = useCallback((next: boolean | ((prev: boolean) => boolean)) => {
        const current = Boolean((useViewerStore.getState() as any).resultCardMinimized);
        const resolved = typeof next === 'function' ? (next as (prev: boolean) => boolean)(current) : next;
        useViewerStore.setState({ resultCardMinimized: resolved } as any);
    }, []);
    const aiScenariosSectionVisible = useViewerStore((s: any) => Boolean(s.aiScenariosSectionVisible));
    const aiDesignsDataSource = useViewerStore(
        (s: any) => (s.aiDesignsDataSource as AiDesignsDataSource | undefined) ?? null,
    );
    const aiLoadingStep = useViewerStore((s: any) => (typeof (s as any).aiLoadingStep === 'string' ? (s as any).aiLoadingStep : ''));
    const aiRunStartMs = useViewerStore((s: any) => (typeof (s as any).aiRunStartMs === 'number' ? (s as any).aiRunStartMs : null));
    const aiRunElapsedMs = useViewerStore((s: any) => (typeof s.aiRunElapsedMs === 'number' ? s.aiRunElapsedMs : null));
    const reRunningTreeScenarios = useViewerStore((s: any) => Boolean(s.reRunningTreeScenarios));
    const treeScenariosNeedRerun = useViewerStore((s: any) => Boolean(s.treeScenariosNeedRerun));
    const setSelectedRoofCombinationKey = useCallback((next: string) => {
        const current = ((useViewerStore.getState() as any).selectedRoofCombinationKey ?? 'default') as string;
        if (current === next) return;
        useViewerStore.setState({ selectedRoofCombinationKey: next } as any);
    }, []);
    const treeScenariosByLayoutKey = useViewerStore(
        (s) => s.aiModuleFillingTreeScenariosByLayoutKey ?? STABLE_EMPTY_OBJECT,
    );
    const setTreeScenariosByLayoutKey = useCallback((next: (prev: Record<string, any[]>) => Record<string, any[]>) => {
        const prev = ((useViewerStore.getState() as any).aiModuleFillingTreeScenariosByLayoutKey || {}) as Record<string, any[]>;
        const resolved = next(prev);
        useViewerStore.setState({ aiModuleFillingTreeScenariosByLayoutKey: resolved } as any);
    }, []);
    const setLayoutKeyWhenTreeScenariosRun = useCallback((next: string) => {
        useViewerStore.setState({ layoutKeyWhenTreeScenariosRun: next } as any);
    }, []);
    const setReRunningTreeScenarios = useCallback((next: boolean) => {
        useViewerStore.setState({ reRunningTreeScenarios: next } as any);
    }, []);
    const setAiError = useCallback((next: string | null) => {
        useViewerStore.setState({ aiError: next } as any);
    }, []);
    const setTreeReprocessInProgress = useCallback((next: boolean) => {
        useViewerStore.setState({ treeReprocessInProgress: next } as any);
    }, []);
    const simulateLayoutForRoofIds = useCallback(
        async (
            roofFaceIds: string[],
            opts?: {
                dir?: 'port' | 'land' | 'both' | null;
                minEff?: number | null;
                layoutCacheKey?: string;
            },
        ): Promise<any> => {
            const self = (sceneViewerProp ?? (useViewerStore.getState() as any).sceneViewer) as any;
            if (!self || !roofFaceIds.length) return { production: 0, modules: 0, efficiency: null, weightedSolarAverage: null };
            clearAllModuleMeshes(self);
            const dir = opts?.dir ?? null;
            const minEff = normalizeLayoutMinEff(opts?.minEff);
            addModulesToRoofsByIds(self, roofFaceIds, dir as any);
            await SetModuleGraph(self);
            if (minEff != null) {
                removeGreyModulesAndSingleModuleRoofs(self, roofFaceIds, minEff);
            } else {
                removeGreyModulesAndSingleModuleRoofs(self, roofFaceIds);
            }
            await SetModuleGraph(self);
            const state = useViewerStore.getState() as any;
            // Capture from the live meshes (source of truth), not the store's
            // `savedRoofModulesLayout`, which is not refreshed by this fill path.
            const modulesLayout = captureLiveModulesLayout(self);
            if (opts?.layoutCacheKey) {
                useViewerStore.setState((prev: any) => ({
                    layoutModulesLayoutByKey: {
                        ...(prev.layoutModulesLayoutByKey || {}),
                        [opts.layoutCacheKey!]: modulesLayout,
                    },
                }));
            }
            return {
                production: typeof state.actualWattage === 'number' ? state.actualWattage : 0,
                modules: typeof state.modeCount === 'number' ? state.modeCount : 0,
                efficiency: typeof state.efficiency === 'number' ? state.efficiency : null,
                weightedSolarAverage:
                    typeof state.weightedSolarAverage === 'number' ? state.weightedSolarAverage : null,
                modulesLayout,
            };
        },
        [sceneViewerProp],
    );
    const handleReRunTreeScenariosLocal = useCallback(
        (layoutKey?: string, onComplete?: () => void) => {
            runReRunTreeScenariosAction({
                layoutKey,
                onComplete,
                selectedRoofCombinationKey,
                sceneViewer,
                reRunningTreeScenarios,
                defaultMaxfillRoofIds,
                bestRoofsForLayout,
                roofCombinationsFromAi,
                simulateLayoutForRoofIds,
                runGhostScenariosAndSave,
                setTreeScenariosByLayoutKey,
                setLayoutModulesByKey,
                setLayoutKeyWhenTreeScenariosRun,
                setReRunningTreeScenarios,
                setIsDashboardOpen: (next) => setIsDashboardOpen(next),
                setAiError,
                setTreeReprocessInProgress,
            });
        },
        [
            selectedRoofCombinationKey,
            sceneViewer,
            reRunningTreeScenarios,
            defaultMaxfillRoofIds,
            bestRoofsForLayout,
            roofCombinationsFromAi,
            simulateLayoutForRoofIds,
            setTreeScenariosByLayoutKey,
            setLayoutModulesByKey,
            setLayoutKeyWhenTreeScenariosRun,
            setReRunningTreeScenarios,
            setIsDashboardOpen,
            setAiError,
            setTreeReprocessInProgress,
        ],
    );
    const handleReRunTreeScenarios =
        handleReRunTreeScenariosPropOrUndefined ?? handleReRunTreeScenariosLocal;
    const designAndShadeReady = Boolean(loadingCompleted && !refreshingShadeFromStore);
    const productionValuesReady = useProductionCalculationReady();
    const modeCountForRunAi = useViewerStore((s) => s.modeCount);
    const shadeCalculatingIdx = useViewerStore((s) => s.shadeCalculatingIdx);
    const shadeCalculatingLength = useViewerStore((s) => s.shadeCalculatingLength);
    const siteViewerCalculationsPending = useViewerStore((s) => s.siteViewerCalculationsPending);
    const siteModelLoadPending = useViewerStore((s) => s.siteModelLoadPending);
    const siteModelLoadInProgress = useViewerStore((s) => s.siteModelLoadInProgress);
    const hasPrimaryDesign = Array.isArray(designListFromStore) && designListFromStore.length > 0;
    const hasSiteDesignDetails = Array.isArray(siteDesignDetailsFromStore) && siteDesignDetailsFromStore.length > 0;
    const runAiReady = Boolean(
        designAndShadeReady &&
            productionValuesReady &&
            ((Array.isArray(roofInfoArr) && roofInfoArr.length > 0) || Number(modeCountForRunAi) > 0),
    );
    const runAiBlockedStatus = useRunAiBlockedStatus({
        runAiReady,
        aiLoading,
        loadingCompleted,
        refreshingShade: refreshingShadeFromStore,
        productionValuesReady,
        shadeCalculatingIdx,
        shadeCalculatingLength,
        siteModelLoadPending: siteModelLoadPending || siteModelLoadInProgress,
        siteViewerCalculationsPending,
    });
    // const isDashboardDataLoading = !designAndShadeReady && !hasPrimaryDesign && !hasSiteDesignDetails;
    const moduleList = useViewerStore((s) => s.moduleList ?? STABLE_EMPTY_ARRAY);
    const activeModule = useViewerStore((s) => s.activeModule);
    const [modulePickerModalOpen, setModulePickerModalOpen] = useState(false);
    const [modulePickerQuery, setModulePickerQuery] = useState('');
    const onModuleChange = useCallback((next: any) => {
        useViewerStore.setState({ activeModule: next ?? undefined });
    }, []);

    /** Update store + refresh module placement on roofs (same behavior as former module Select). */
    const applyModuleToDesign = useCallback(
        (next: any) => {
            onModuleChange(next);
            if (sceneViewer && next && Array.isArray((sceneViewer as any).roofMeshArr) && (sceneViewer as any).roofMeshArr.length > 0) {
                try {
                    addModulesToAllRoofsMaxFill(sceneViewer);
                } catch {
                    // non-fatal
                }
            }
        },
        [onModuleChange, sceneViewer],
    );

    const filteredModulesForPicker = useMemo(() => {
        const q = modulePickerQuery.trim().toLowerCase();
        if (!q) return moduleList;
        return moduleList.filter((m: any) => String(m?.text ?? '').toLowerCase().includes(q));
    }, [moduleList, modulePickerQuery]);
    const layoutSnapshotsByKey = useViewerStore((s) => s.layoutSnapshotsByKey ?? STABLE_EMPTY_OBJECT);
    const aiLayoutVariantSnapshots = useViewerStore(
        (s) => ((s as any).aiLayoutVariantSnapshots ?? STABLE_EMPTY_OBJECT) as Record<string, unknown>,
    );
    const modeCount = useViewerStore((s) => s.modeCount);

    const capabilitiesReady = user != null;
    const openCanvasFirst = shouldOpenCanvasOnConfigureLoad();
    const dashboardFirstOnLoad =
        capabilitiesReady && hasCapability('Show_OneButton') && !openCanvasFirst;
    const landingPreferenceAppliedRef = useRef(false);

    useOpportunityInitialization({
        enabled: dashboardFirstOnLoad,
        salesForceId,
        refreshKey: oppRefreshKey,
        // Opportunity Information only — do not gate the dashboard on oneDRAW draw/shade.
        waitForSiteDesign: false,
    });

    const handleRetryOpportunityInitialization = useCallback(() => {
        setInitialDashboardReady(false);
        setOppRefreshKey((prev) => prev + 1);
    }, []);

    useEffect(() => {
        setInitialDashboardReady(false);
    }, [salesForceId]);

    const overlayVisible =
        dashboardFirstOnLoad &&
        isDashboardOpen &&
        (isOpportunityInitializing || Boolean(opportunityInitializationError));

    useEffect(() => {
        if (siteViewerUnavailable) {
            setInitialDashboardReady(true);
            return;
        }

        if (!dashboardFirstOnLoad) {
            if (designAndShadeReady) {
                setInitialDashboardReady(true);
            }
            return;
        }

        const sfid = salesForceId?.trim();
        const oppInitDone =
            !isOpportunityInitializing &&
            Boolean(sfid) &&
            opportunityInitializedSfid === sfid;

        if (oppInitDone) {
            setInitialDashboardReady(true);
        }
    }, [
        siteViewerUnavailable,
        dashboardFirstOnLoad,
        designAndShadeReady,
        isOpportunityInitializing,
        opportunityInitializedSfid,
        salesForceId,
    ]);

    useEffect(() => {
        if (!capabilitiesReady) return;
        if (landingPreferenceAppliedRef.current) return;
        landingPreferenceAppliedRef.current = true;
        const launchAction = consumePreContractLaunchAction();
        if (openCanvasFirst || launchAction === 'runAi' || launchAction === 'openDesign') {
            clearPreContractOpenCanvasFlag();
            setIsDashboardOpen(false);
            return;
        }
        if (launchAction === 'runPricing') {
            setIsDashboardOpen(true);
            return;
        }
        setIsDashboardOpen(dashboardFirstOnLoad);
    }, [capabilitiesReady, dashboardFirstOnLoad, openCanvasFirst, setIsDashboardOpen]);

    // Batch AI: hard-stop watchdog. If user clicks "Stop Processing" on /batch-ai,
    // immediately navigate away from the embedded runner to abort any in-flight work.
    useEffect(() => {
        if (!isBatchMode) return;
        const interval = window.setInterval(() => {
            try {
                if (sessionStorage.getItem('batchStopRequested') === 'true') {
                    window.location.assign('/batch-ai');
                    return;
                }
            } catch {
                // ignore
            }
            const bState = loadBatchState();
            if (bState && !bState.isProcessing) {
                window.location.assign('/batch-ai');
            }
        }, 250);
        return () => window.clearInterval(interval);
    }, [isBatchMode]);

    useEffect(() => {
        if (!isBatchMode || batchAiTriggered || aiLoading) return;
        // If the user hit "Stop Processing" on /batch-ai, do not trigger AI.
        if (typeof window !== 'undefined' && sessionStorage.getItem('batchStopRequested') === 'true') {
            navigate('/batch-ai');
            return;
        }
        // Also honor persisted batch state.
        const bState = loadBatchState();
        if (bState && !bState.isProcessing) {
            navigate('/batch-ai');
            return;
        }
        const ready = Boolean(
            loadingCompleted &&
                !refreshingShadeFromStore &&
                productionValuesReady &&
                ((Array.isArray(roofInfoArr) && roofInfoArr.length > 0) || Number(modeCountForRunAi) > 0),
        );
        if (!ready) return;
        const runAi = batchRunAiFallbackRef.current;
        if (!runAi) return;
        const timer = setTimeout(() => {
            if (typeof window !== 'undefined' && sessionStorage.getItem('batchStopRequested') === 'true') {
                navigate('/batch-ai');
                return;
            }
            const latest = loadBatchState();
            if (latest && !latest.isProcessing) {
                navigate('/batch-ai');
                return;
            }
            setBatchAiTriggered(true);
            runAi();
        }, 2000);
        return () => clearTimeout(timer);
    }, [isBatchMode, batchAiTriggered, aiLoading, loadingCompleted, refreshingShadeFromStore, productionValuesReady, roofInfoArr, modeCountForRunAi]);

    useEffect(() => {
        if (!isBatchMode || !batchAiTriggered || batchResultCaptured) return;
        if (aiLoading || (!aiResult && !aiError)) return;

        setBatchResultCaptured(true);
        const batchState = loadBatchState();
        if (!batchState) return;
        if (!batchState.isProcessing) {
            navigate('/batch-ai');
            return;
        }

        const processedIndex = batchState.currentIndex;
        const currentRecord = batchState.records[batchState.currentIndex];
        const startTime = Number(sessionStorage.getItem('batchSiteStartTime') || Date.now());

        if (aiResult) {
            const storeState = useViewerStore.getState();
            const result = captureAIResults(currentRecord, aiResult, storeState, startTime);
            batchState.results[batchState.currentIndex] = result;
        } else if (aiError) {
            batchState.results[batchState.currentIndex] = {
                ...batchState.results[batchState.currentIndex],
                status: 'error',
                error: aiError,
                processingTimeMs: Date.now() - startTime,
            };
        }

        batchState.currentIndex += 1;
        const stopAfterIndexRaw = sessionStorage.getItem('batchStopAfterIndex');
        const stopAfterIndex = stopAfterIndexRaw != null ? Number(stopAfterIndexRaw) : null;
        if (stopAfterIndex != null && !Number.isNaN(stopAfterIndex) && processedIndex === stopAfterIndex) {
            batchState.isProcessing = false;
            batchState.completedAt = Date.now();
            try { sessionStorage.removeItem('batchStopAfterIndex'); } catch { /* ignore */ }
            saveBatchState(batchState);
            navigate('/batch-ai');
            return;
        }

        if (batchState.currentIndex >= batchState.records.length) {
            batchState.isProcessing = false;
            batchState.completedAt = Date.now();
            saveBatchState(batchState);
            navigate('/batch-ai');
            return;
        }
        saveBatchState(batchState);
        batchState.results[batchState.currentIndex] = {
            ...batchState.results[batchState.currentIndex],
            status: 'processing',
        };
        saveBatchState(batchState);
        const nextRecord = batchState.records[batchState.currentIndex];
        window.location.assign(`/configure?sfid=${nextRecord.salesforceId}&batch=true`);
    }, [isBatchMode, batchAiTriggered, batchResultCaptured, aiLoading, aiResult, aiError, navigate]);

    useEffect(() => {
        if (!isBatchMode || batchResultCaptured) return;
        // If stop was requested, cancel the timeout handling and return to the batch page.
        if (typeof window !== 'undefined' && sessionStorage.getItem('batchStopRequested') === 'true') {
            navigate('/batch-ai');
            return;
        }
        // Configurable per-site timeout for batch AI runs.
        // Set VITE_BATCH_AI_TIMEOUT_MINUTES in env; defaults to 15 minutes.
        const configuredTimeoutMinutes = Number((import.meta as any).env?.VITE_BATCH_AI_TIMEOUT_MINUTES);
        const timeoutMinutes = Number.isFinite(configuredTimeoutMinutes) && configuredTimeoutMinutes > 0
            ? configuredTimeoutMinutes
            : 15;
        const BATCH_TIMEOUT_MS = timeoutMinutes * 60 * 1000;
        const startTime = Number(sessionStorage.getItem('batchSiteStartTime') || Date.now());
        const elapsedMs = Math.max(0, Date.now() - startTime);
        const remainingMs = Math.max(0, BATCH_TIMEOUT_MS - elapsedMs);
        const timer = setTimeout(() => {
            if (batchResultCaptured) return;
            if (typeof window !== 'undefined' && sessionStorage.getItem('batchStopRequested') === 'true') {
                navigate('/batch-ai');
                return;
            }
            setBatchResultCaptured(true);
            setBatchAiTriggered(true);

            const bState = loadBatchState();
            if (!bState || bState.currentIndex >= bState.records.length) return;
            if (!bState.isProcessing) {
                navigate('/batch-ai');
                return;
            }
            const processedIndex = bState.currentIndex;
            const siteStartTime = Number(sessionStorage.getItem('batchSiteStartTime') || Date.now());
            bState.results[bState.currentIndex] = {
                ...bState.results[bState.currentIndex],
                status: 'error',
                error: `Timed out after ${timeoutMinutes} minutes`,
                processingTimeMs: Date.now() - siteStartTime,
            };
            bState.currentIndex += 1;

            const stopAfterIndexRaw = sessionStorage.getItem('batchStopAfterIndex');
            const stopAfterIndex = stopAfterIndexRaw != null ? Number(stopAfterIndexRaw) : null;
            if (stopAfterIndex != null && !Number.isNaN(stopAfterIndex) && processedIndex === stopAfterIndex) {
                bState.isProcessing = false;
                bState.completedAt = Date.now();
                try { sessionStorage.removeItem('batchStopAfterIndex'); } catch { /* ignore */ }
                saveBatchState(bState);
                navigate('/batch-ai');
                return;
            }
            if (bState.currentIndex >= bState.records.length) {
                bState.isProcessing = false;
                bState.completedAt = Date.now();
                saveBatchState(bState);
                navigate('/batch-ai');
                return;
            }
            const nextRec = bState.records[bState.currentIndex];
            bState.results[bState.currentIndex] = { ...bState.results[bState.currentIndex], status: 'processing' };
            saveBatchState(bState);
            window.location.assign(`/configure?sfid=${nextRec.salesforceId}&batch=true`);
        }, remainingMs);
        return () => clearTimeout(timer);
    }, [isBatchMode, batchResultCaptured, navigate]);

    const SFannualUsage = useViewerStore((s) => s.SFannualUsage);

    const annualUsageCapKwh =
        typeof dashboardAnnualUsage === 'number' && Number.isFinite(dashboardAnnualUsage) && dashboardAnnualUsage > 0
            ? dashboardAnnualUsage
            : null;
    const activeLayoutOverUsage = isLayoutProductionOverAnnualUsage(activeLayoutProduction, annualUsageCapKwh);

    // Disabled while stabilizing dashboard state loops.

    // Stable per-session proposal IDs keyed by card key so each design card
    // gets a real unique identifier that does not change on re-render.
    const proposalIdByCardKeyRef = useRef<Record<string, string>>({});
    /** Closes layout-options sub-panel / modal synchronously before Re-run AI (ref set after state hooks below). */
    const closeLayoutOptionsUiRef = useRef<() => void>(() => {});
    const getProposalIdForCard = (cardKey: string): string => {
        const map = proposalIdByCardKeyRef.current;
        if (!map[cardKey]) {
            const uuid =
                (globalThis as any)?.crypto?.randomUUID?.() ??
                `p-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
            map[cardKey] = uuid;
        }
        return map[cardKey];
    };

    const getSelectedLayoutTitle = () => {
        if (selectedRoofCombinationKey === 'default') return 'oneDRAW Default Maxfill';
        const c = roofCombinationsFromAi.find((c: any) => c.name === selectedRoofCombinationKey);
        return c && /^(maximum\s+production|max\s+production(\s+with\s+best\s+roofs)?)$/i.test((c.name || '').trim()) ? 'Max Production with Best Roofs' : (c?.name ?? selectedRoofCombinationKey);
    };

    const selectedDesignForPricing = useOneButtonStore((s) => s.selectedDesignForPricing);
    const setSelectedDesignForPricing = useOneButtonStore((s) => s.setSelectedDesignForPricing);
    const setPricingModalFetchTrigger = useOneButtonStore((s) => s.setPricingModalFetchTrigger);
    const pricingSelectionLoading = useOneButtonStore((s) => s.pricingSelectionLoading);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const theme = useOneButtonStore((s) => s.theme);
    const hoverJobId = useOneButtonStore((s) => s.hoverJobId);
    const hoverJobAvailable = isHoverJobAvailable(hoverJobId);
    const dashboardLayoutVariant = useViewerStore((s) => s.dashboardLayoutVariant);

    const savedAIDesigns = useOneButtonStore((s) => s.savedAIDesigns);
    const addAIDesign = useOneButtonStore((s) => s.addAIDesign);
    const removeAIDesign = useOneButtonStore((s) => s.removeAIDesign);
    const activeAIDesignId = useOneButtonStore((s) => s.activeAIDesignId);
    const setActiveAIDesignId = useOneButtonStore((s) => s.setActiveAIDesignId);
    const activeSubScenarioId = useOneButtonStore((s) => s.activeSubScenarioId);
    const setActiveSubScenarioId = useOneButtonStore((s) => s.setActiveSubScenarioId);
    const addTreeCutSubScenario = useOneButtonStore((s) => s.addTreeCutSubScenario);
    const removeTreeCutSubScenario = useOneButtonStore((s) => s.removeTreeCutSubScenario);
    const setPriceQuoteOverrides = useOneButtonStore((s) => s.setPriceQuoteOverrides);
    const setPriceQuoteDetails = useOneButtonStore((s) => s.setPriceQuoteDetails);
    const setSystemDesignQuotesLoading = useOneButtonStore((s) => s.setSystemDesignQuotesLoading);
    const quotesRefreshTrigger = useOneButtonStore((s) => s.quotesRefreshTrigger);
    const systemDesignRefreshTrigger = useOneButtonStore((s) => s.systemDesignRefreshTrigger);
    const setSystemDesign = useOneButtonStore((s) => s.setSystemDesign);
    const setSystemDesignLosses = useOneButtonStore((s) => s.setSystemDesignLosses);
    const setDesignImage = useOneButtonStore((s) => s.setDesignImage);
    /** Same numeric Opportunity_Id used by RetrieveUtilityInformation (set from opportunity details). */
    const opportunityID = useOneButtonStore((s) => s.opportunityID);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);

    const loadSavedAiDesignsIfNeeded = useCallback(async (sfid?: string): Promise<boolean> => {
        // Defer while contacts modal is open — GetAiResult storms freeze typing.
        if (useOneButtonStore.getState().dashboardContactsModalOpen) return false;
        if (Boolean((useViewerStore.getState() as any).aiLoading)) return false;
        if (hasHydratedAiLayoutCatalogInStore()) return true;
        const key =
            (sfid && String(sfid).trim()) ||
            (opportunityDetails?.SalesForce_Id && String(opportunityDetails.SalesForce_Id).trim()) ||
            getSalesforceIdForAiCache() ||
            '';
        if (!key) return false;
        if (typeof window !== 'undefined') {
            const current = (sessionStorage.getItem('SalesForceId') || '').trim();
            if (!current || current !== key) {
                sessionStorage.setItem('SalesForceId', key);
            }
        }
        syncAiResultCacheTabRegistration(key);
        await handleLoadSavedDesigns({ showLoading: false });
        // One attempt is enough — empty catalog must not restart GetAiResult forever.
        return true;
    }, [handleLoadSavedDesigns, opportunityDetails?.SalesForce_Id]);

    // Clear a stale saved-scenarios spinner when switching opportunities/sites.
    useEffect(() => {
        lastAutoHydrateSavedAiDesignsSfRef.current = null;
        useViewerStore.setState({ loadingSavedResult: false } as any);
        const sfid =
            (opportunityDetails?.SalesForce_Id && String(opportunityDetails.SalesForce_Id).trim()) ||
            getSalesforceIdForAiCache() ||
            null;
        syncAiResultCacheTabRegistration(sfid);
    }, [opportunityDetails?.SalesForce_Id]);

    useEffect(() => {
        if (isBatchMode || !hasCapability('Show_AI_Scenarios')) return;
        if (!loadingCompleted || refreshingShadeFromStore) return;
        if (hasHydratedAiLayoutCatalogInStore()) return;
        if (useOneButtonStore.getState().dashboardContactsModalOpen) return;
        const resolvedSfid =
            (opportunityDetails?.SalesForce_Id && String(opportunityDetails.SalesForce_Id).trim()) ||
            getSalesforceIdForAiCache() ||
            '';

        void loadSavedAiDesignsIfNeeded(resolvedSfid || undefined);
    }, [
        isBatchMode,
        loadingCompleted,
        refreshingShadeFromStore,
        opportunityDetails?.SalesForce_Id,
        loadSavedAiDesignsIfNeeded,
    ]);

    /** After refresh the 3D viewer mounts after the first cache read — retry legacy winner selection then. */
    useEffect(() => {
        if (isBatchMode || !hasCapability('Show_AI_Scenarios')) return;
        if (!loadingCompleted || refreshingShadeFromStore || !sceneViewer) return;
        if (hasHydratedAiLayoutCatalogInStore()) return;
        if (useOneButtonStore.getState().dashboardContactsModalOpen) return;

        const resolvedSfid =
            (opportunityDetails?.SalesForce_Id && String(opportunityDetails.SalesForce_Id).trim()) ||
            getSalesforceIdForAiCache() ||
            '';

        void (async () => {
            await loadSavedAiDesignsIfNeeded(resolvedSfid || undefined);
            tryReapplyCategoryBestLayoutsFromStore();
        })();
    }, [
        isBatchMode,
        loadingCompleted,
        refreshingShadeFromStore,
        sceneViewer,
        opportunityDetails?.SalesForce_Id,
        loadSavedAiDesignsIfNeeded,
    ]);

    useEffect(() => {
        if (isBatchMode || !hasCapability('Show_AI_Scenarios')) return;
        if (!loadingCompleted || refreshingShadeFromStore) return;
        const hydrateIfPossible = () => {
            if (useOneButtonStore.getState().dashboardContactsModalOpen) return false;
            if (Boolean((useViewerStore.getState() as any).aiLoading)) return false;
            const key =
                (opportunityDetails?.SalesForce_Id && String(opportunityDetails.SalesForce_Id).trim()) ||
                getSalesforceIdForAiCache() ||
                '';
            if (!key) return false;
            if (hasHydratedAiLayoutCatalogInStore()) {
                lastAutoHydrateSavedAiDesignsSfRef.current = key;
                return true;
            }
            if (lastAutoHydrateSavedAiDesignsSfRef.current === key) {
                return false;
            }
            lastAutoHydrateSavedAiDesignsSfRef.current = key;
            void loadSavedAiDesignsIfNeeded(key).then((didAttempt) => {
                // didAttempt false => deferred (contacts open / AI loading) — allow a later retry.
                if (!didAttempt) {
                    lastAutoHydrateSavedAiDesignsSfRef.current = null;
                }
            });
            return true;
        };

        // In some navigation flows SalesForceId is written to sessionStorage slightly
        // after this component effect runs. Poll briefly so saved designs load by default.
        if (hydrateIfPossible()) return;

        let attempts = 0;
        const maxAttempts = 16; // ~8 seconds at 500ms
        const timer = window.setInterval(() => {
            attempts += 1;
            const hydrated = hydrateIfPossible();
            if (hydrated || attempts >= maxAttempts) {
                window.clearInterval(timer);
            }
        }, 500);

        return () => window.clearInterval(timer);
    }, [
        isBatchMode,
        loadingCompleted,
        refreshingShadeFromStore,
        opportunityDetails?.SalesForce_Id,
        loadSavedAiDesignsIfNeeded,
    ]);

    useEffect(() => {
        if (isBatchMode) return;
        if (!loadingCompleted || refreshingShadeFromStore) return;
        const sfid = getSalesforceIdForAiCache();
        if (!sfid) return;
        if (!hasUsableAiDesignCatalogInStore()) return;
        const state = useViewerStore.getState() as any;
        if (Boolean(state.loadingSavedResult) || Boolean(state.aiLoading)) return;

        const timer = window.setTimeout(() => {
            if (!hasUsableAiDesignCatalogInStore()) return;
            const latest = useViewerStore.getState() as any;
            void saveLocalAiResultCache({
                layoutSnapshots: latest.layoutSnapshotsByKey ?? null,
                variantSnapshots: latest.aiLayoutVariantSnapshots ?? null,
                aiScenarios: buildAiScenariosCacheBlobForLocalPersist(),
                treeCuttingScenarios: latest.aiModuleFillingTreeScenariosByLayoutKey ?? {},
                aiWishlist: savedAIDesigns ?? [],
                aiResult: typeof latest.aiResult === 'string' ? latest.aiResult : null,
            }, sfid);
        }, 1500);

        return () => window.clearTimeout(timer);
    }, [
        isBatchMode,
        loadingCompleted,
        refreshingShadeFromStore,
        aiModuleFillingScenarios,
        layoutProductionByKey,
        layoutModulesByKey,
        layoutMetricsByKey,
        treeScenariosByLayoutKey,
        categoryMainLayoutByCategory,
        savedAIDesigns,
    ]);
    const financePartnerSyncModalOpen = useOneButtonStore((s) => s.financePartnerSyncModalOpen);
    const dashboardContactsModalOpen = useOneButtonStore((s) => s.dashboardContactsModalOpen);
    const setFinancePartnerSyncModalOpen = useOneButtonStore((s) => s.setFinancePartnerSyncModalOpen);
    const leadSyncFormOpen = useOneButtonStore((s) => s.leadSyncFormOpen);
    const setLeadSyncFormOpen = useOneButtonStore((s) => s.setLeadSyncFormOpen);
    const solarUtilityData = useOneButtonStore((s) => s.solarUtilityData);
    const priceQuoteOverrides = useOneButtonStore((s) => s.priceQuoteOverrides);

    const [addTitleContactsModalOpen, setAddTitleContactsModalOpen] = useState(false);
    const [blackKnightModalOpen, setBlackKnightModalOpen] = useState(false);
    const [blackKnightModalData, setBlackKnightModalData] = useState<any>(null);

    const [priceQuote, setPriceQuote] = useState(null as any);
    const [titleSearchData, setTitleSearchData] = useState<any[] | null>(null);
    // Viewer-level elapsed time display uses `aiRunElapsedMs` from the store.

    const restoreDefaultLayoutFromSnapshot = useCallback(async () => {
        if (!sceneViewer) return;
        addModulesToAllRoofsMaxFill(sceneViewer);
        await SetModuleGraph(sceneViewer as any);
    }, [sceneViewer]);

    const waitForSceneIdle = useCallback(async (timeoutMs = 5000) => {
        const start = Date.now();
        // eslint-disable-next-line no-constant-condition
        while (true) {
            if (Date.now() - start >= timeoutMs) break;
            const state = useViewerStore.getState() as any;
            if (state.loadingCompleted && !state.refreshingShade) break;
            await new Promise((resolve) => setTimeout(resolve, 200));
        }
    }, []);

    const getRoofIdsForLayoutKey = useCallback(
        (key: string): string[] =>
            resolveRoofIdsForLayoutKey(key, {
                defaultMaxfillRoofIds,
                bestRoofIds: bestRoofsForLayout.roofFaceIds,
            }),
        [defaultMaxfillRoofIds, bestRoofsForLayout],
    );

    const executeAiPipelineAfterPayloadChoice = useCallback(async (params: {
            apiKey: string;
            startMs: number;
            layoutAtRunStart: string;
            scenarios: any[];
            selection: AiRunPayloadSelection;
        }) => {
            const { apiKey, startMs: start, layoutAtRunStart, scenarios, selection } = params;
            const scenariosForAi = applyAiRunScenarioSelection(scenarios, selection.scenarioIndices);
            const layoutVariantOptions = {
                includeGlobalAllRoofs: selection.includeGlobalAllRoofs,
                includeGlobalBestSubsets: selection.includeGlobalBestSubsets,
                // Post-Gemini pipeline generates per-category variants on the correct roof sets.
                includeCategoryLayoutCards: false,
            };
            beginBatchLayoutSiteCalc();
            let pipelineSucceeded = false;
            let geminiError: string | null = null;
            try {
                useViewerStore.setState({ categoryMainLayoutByCategory: {} } as any);
                const layoutVariantsSummary = await runGenerateLayoutVariantsAction({
                    sceneViewer,
                    defaultMaxfillRoofIds,
                    bestRoofsForLayout,
                    restoreDefaultLayoutFromSnapshot,
                    setAiLoadingStep,
                    layoutVariantOptions,
                });
                try {
                    await runAiFinalizeAction({
                        apiKey,
                        scenarios: scenariosForAi,
                        scenariosForTreeScenariosRecord: scenarios,
                        layoutAtRunStart,
                        layoutVariantsSummary,
                        setTreeScenariosByLayoutKey,
                        setAiLoadingStep,
                        setAiResult,
                        setAiError,
                        setAiLoading,
                        setAiRunElapsedMs,
                        setLayoutKeyWhenTreeScenariosRun,
                        setIsDashboardOpen: (next: boolean) => setIsDashboardOpen(next),
                        sceneViewer,
                        startMs: start,
                        deferCompletion: true,
                    });
                } catch (e) {
                    geminiError = e instanceof Error ? e.message : String(e);
                }
                await runPostAiLayoutPipelineAction({
                    sceneViewer,
                    defaultMaxfillRoofIds,
                    bestRoofsForLayout,
                    selectedRoofCombinationKey,
                    restoreDefaultLayoutFromSnapshot,
                    simulateLayoutForRoofIds,
                    setSimulatingLayouts,
                    setSimulatingLayoutIndex,
                    setSimulatingLayoutTotal,
                    setLayoutMetricsByKey,
                    setLayoutProductionByKey,
                    setLayoutModulesByKey,
                    setLayoutSimulationTimeByKey,
                    setAiLoadingStep,
                    roofIdsByLayoutKey: getRoofIdsForLayoutKey,
                });
                const sfid = getSalesforceIdForAiCache();
                if (sfid) {
                    const stateAfterPipeline = useViewerStore.getState() as any;
                    void saveLocalAiResultCache(
                        {
                            layoutSnapshots: stateAfterPipeline.layoutSnapshotsByKey ?? null,
                            variantSnapshots: stateAfterPipeline.aiLayoutVariantSnapshots ?? null,
                            aiScenarios: buildAiScenariosCacheBlobForLocalPersist(),
                            treeCuttingScenarios: stateAfterPipeline.aiModuleFillingTreeScenariosByLayoutKey ?? {},
                            aiWishlist: [],
                            aiResult: typeof stateAfterPipeline.aiResult === 'string' ? stateAfterPipeline.aiResult : null,
                        },
                        sfid,
                        { force: true },
                    );
                }
                pipelineSucceeded = true;
                useViewerStore.setState({ aiDesignsDataSource: 'run' } as any);
            } catch (e) {
                setAiError(e instanceof Error ? e.message : String(e));
            } finally {
                if (geminiError) {
                    if (hasUsableAiDesignCatalogInStore()) {
                        setAiError(null);
                    } else {
                        setAiError(geminiError);
                    }
                }
                await forceEndBatchLayoutSiteCalc(sceneViewer);
                setAiLoading(false);
                setAiLoadingStep('');
                useViewerStore.setState({ isBgCalc: false });
                setAiRunElapsedMs(Date.now() - start);
                setLayoutKeyWhenTreeScenariosRun(layoutAtRunStart);
                if (pipelineSucceeded) {
                    try {
                        sceneViewer?.hideObject?.(HideObjectType.GhostTree, (useViewerStore.getState() as any).isHideAllGhostTrees);
                    } catch {
                        // ignore
                    }
                }
            }
        }, [
            sceneViewer,
            defaultMaxfillRoofIds,
            bestRoofsForLayout,
            restoreDefaultLayoutFromSnapshot,
            setAiLoadingStep,
            setTreeScenariosByLayoutKey,
            setAiResult,
            setAiError,
            setAiLoading,
            setAiRunElapsedMs,
            setLayoutKeyWhenTreeScenariosRun,
            setIsDashboardOpen,
            selectedRoofCombinationKey,
            simulateLayoutForRoofIds,
            setSimulatingLayouts,
            setSimulatingLayoutIndex,
            setSimulatingLayoutTotal,
            setLayoutMetricsByKey,
            setLayoutProductionByKey,
            setLayoutModulesByKey,
            setLayoutSimulationTimeByKey,
            getRoofIdsForLayoutKey,
        ]);

    const runAiDashboardReturnPendingRef = useRef(false);

    const handleRetryAiWithPayloadSelection = useCallback(async (selection: AiRunPayloadSelection) => {
            const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
            if (!apiKey) {
                setAiError('VITE_GEMINI_API_KEY is not set.');
                return;
            }
            const scenarios = buildAiModuleFillingInput();
            if (!scenarios.length) {
                setAiError('No scenario data (no roofs or trees). Run shade calculation first.');
                return;
            }
            setAiError(null);
            setAiResult(null);
            invalidateAiResultSessionCache();
            clearAiLayoutSnapshotStateForRun();
            clearWishlistCacheForRun();
            const start = Date.now();
            setAiRunStartMs(start);
            setAiRunElapsedMs(null);
            if (!isBatchMode) runAiDashboardReturnPendingRef.current = true;
            closeLayoutOptionsUiRef.current();
            setAiLoading(true);
            useViewerStore.setState({ loadingSavedResult: false } as any);
            clearPendingUnghostAlert();
            try {
                sceneViewer?.removeViewerAlert?.();
            } catch {
                // ignore
            }
            setAiLoadingStep('Preparing production calculations...');
            await ensureProductionReadyForBackgroundWork();
            setAiLoadingStep('Trying layout variants (portrait, landscape, efficiency)...');
            const layoutAtRunStart = selectedRoofCombinationKey || 'default';
            await executeAiPipelineAfterPayloadChoice({
                apiKey,
                startMs: start,
                layoutAtRunStart,
                scenarios,
                selection,
            });
        }, []);

    const handleRunAiLocal = useCallback(async () => {
        if (useOneButtonStore.getState().dashboardContactsModalOpen) return;
        if (!isBatchMode) runAiDashboardReturnPendingRef.current = true;
        closeLayoutOptionsUiRef.current();
        const pre = await runAiPreludeAction({
            selectedRoofCombinationKey,
            sceneViewer,
            setAiScenariosSectionVisible: (next: boolean) => setAiScenariosSectionVisible(next),
            setIsDashboardOpen: (next: boolean) => setIsDashboardOpen(next),
            setPathname,
            setAiError,
            setAiResult,
            setAiLoading,
            setAiRunStartMs: (next: number) => setAiRunStartMs(next),
            setAiRunElapsedMs,
            setAiLoadingStep,
            runGhostScenariosAndSave,
            buildAiModuleFillingInput,
            onBeforeAiLoading: () => closeLayoutOptionsUiRef.current(),
        });
        const { shouldContinue, apiKey, startMs: start, layoutAtRunStart, scenarios } = pre;
        if (!shouldContinue) {
            setAiLoading(false);
            setAiLoadingStep('');
            useViewerStore.setState({ isBgCalc: false } as any);
            return;
        }
        const selection = getDefaultAiRunPayloadSelection(scenarios.length);
        await executeAiPipelineAfterPayloadChoice({
            apiKey,
            startMs: start,
            layoutAtRunStart,
            scenarios,
            selection,
        });
    }, [sceneViewer, defaultMaxfillRoofIds, bestRoofsForLayout, restoreDefaultLayoutFromSnapshot, setAiLoadingStep, setTreeScenariosByLayoutKey, setAiResult, setAiError, setAiLoading, setAiRunElapsedMs, setLayoutKeyWhenTreeScenariosRun, setIsDashboardOpen, executeAiPipelineAfterPayloadChoice, isBatchMode]);
    batchRunAiFallbackRef.current = handleRunAiLocal;

    const applyScenarioLayoutForViewer = useCallback(
        (scenarioIndex: number) =>
            applyScenarioLayout(
                scenarioIndex,
                (sceneViewerProp ?? (useViewerStore.getState() as any).sceneViewer) as any,
            ),
        [sceneViewerProp],
    );

    const layoutCandidatesByCategoryRef = useRef<
        Record<string, Array<{ key: string; roofFaceIds?: string[]; dir?: 'port' | 'land' | 'both' | null; minEff?: number | null }>>
    >({});
    const applyRoofSetInViewerRef = useRef<
        (
            roofFaceIds: string[],
            opts?: {
                dir?: 'port' | 'land' | 'both' | null;
                minEff?: number | null;
                deferShadeCalc?: boolean;
            },
        ) => Promise<void>
    >(async () => undefined);

    const handleUseThisLayoutLocal = useCallback(
        async (
            scenarioIndex: number,
            useLayoutKey?: string,
            scenariosFromModal?: any[],
            categoryLabel?: string,
        ) => {
            await runUseThisLayoutAction({
                scenarioIndex,
                useLayoutKey,
                scenariosFromModal,
                selectedRoofCombinationKey,
                setSelectedRoofCombinationKey,
                sceneViewer,
                restoreDefaultLayoutFromSnapshot,
                roofCombinationsFromAi: roofCombinationsFromAi as any,
                layoutCandidatesByCategory: layoutCandidatesByCategoryRef.current,
                categoryMainLayoutByCategory,
                defaultMaxfillRoofIds,
                bestRoofsForLayout: bestRoofsForLayout as any,
                categoryLabel,
                simulateLayoutForRoofIds,
                applyRoofSetInViewer: (...args) => applyRoofSetInViewerRef.current(...args),
                waitForSceneIdle: () => waitForSceneIdle(),
                treeScenariosByLayoutKey,
                savedAIDesigns,
                applyScenarioLayout: applyScenarioLayoutForViewer,
                setTreeScenariosByLayoutKey,
                setApplyingScenarioIndex,
                setApplyingTreeLayoutFromScenario,
            });
        },
        [
            selectedRoofCombinationKey,
            setSelectedRoofCombinationKey,
            sceneViewer,
            sceneViewerProp,
            restoreDefaultLayoutFromSnapshot,
            roofCombinationsFromAi,
            categoryMainLayoutByCategory,
            defaultMaxfillRoofIds,
            bestRoofsForLayout,
            simulateLayoutForRoofIds,
            waitForSceneIdle,
            treeScenariosByLayoutKey,
            savedAIDesigns,
            applyScenarioLayoutForViewer,
            setTreeScenariosByLayoutKey,
            setApplyingScenarioIndex,
        ],
    );

    const handleSimulateLayoutsForKeysLocal = useCallback(
        async (keys: string[]) => {
            await runSimulateLayoutsForKeysAction({
                sceneViewer,
                keys,
                simulatingLayouts,
                defaultMaxfillRoofIds,
                bestRoofsForLayout,
                roofCombinationsFromAi,
                selectedRoofCombinationKey,
                restoreDefaultLayoutFromSnapshot,
                simulateLayoutForRoofIds,
                setSimulatingLayouts,
                setSimulatingLayoutIndex,
                setSimulatingLayoutTotal,
                setLayoutMetricsByKey,
                setLayoutProductionByKey,
                setLayoutModulesByKey,
                setLayoutSimulationTimeByKey,
            });
        },
        [
            sceneViewer,
            simulatingLayouts,
            defaultMaxfillRoofIds,
            bestRoofsForLayout,
            roofCombinationsFromAi,
            selectedRoofCombinationKey,
            restoreDefaultLayoutFromSnapshot,
            simulateLayoutForRoofIds,
            setSimulatingLayouts,
            setSimulatingLayoutIndex,
            setSimulatingLayoutTotal,
            setLayoutMetricsByKey,
            setLayoutProductionByKey,
            setLayoutModulesByKey,
            setLayoutSimulationTimeByKey,
        ],
    );

    const handleRunAi = handleRunAiLocal;
    const handleUseThisLayout = handleUseThisLayoutPropOrUndefined ?? handleUseThisLayoutLocal;
    const handleSimulateLayoutsForKeys = handleSimulateLayoutsForKeysPropOrUndefined ?? handleSimulateLayoutsForKeysLocal;

    const aiCategoryLayoutVariants = useViewerStore(
        (state) => state.aiCategoryLayoutVariants ?? STABLE_EMPTY_OBJECT,
    );
    const aiModuleFillingLayoutVariants = useViewerStore(
        (s: any) => (s.aiModuleFillingLayoutVariants ?? STABLE_EMPTY_ARRAY) as any[],
    );

    useEffect(() => {
        if (!activeAIDesignId || !activeSubScenarioId) return;
        const design = savedAIDesigns.find((d) => d.aiDesignId === activeAIDesignId);
        if (!design) return;
        const sub = (design.treeCutSubScenarios ?? []).find((s) => s.id === activeSubScenarioId);
        if (!sub) return;
        // Keep selected layout synced to active saved tree-cut scenario.
        const currentKey =
            ((useViewerStore.getState() as any).selectedRoofCombinationKey ?? 'default') as string;
        const selectionKey =
            design.layoutKey === 'default' ? 'default' : design.categoryId?.trim() || design.layoutKey;
        if (currentKey !== selectionKey) {
            setSelectedRoofCombinationKey(selectionKey);
        }
    }, [activeAIDesignId, activeSubScenarioId, savedAIDesigns, setSelectedRoofCombinationKey]);

    const checkTitleContactExist = useCallback(async () => {
        if (!opportunityID || !opportunityDetails) return;
        try {
            const addr = opportunityDetails.InstallationAddress;
            const res = await opportunityInfoServices.getCustomerTitleInfooneLead(
                opportunityDetails.DirectLeadID,
                addr?.Street || '',
                (addr?.ZipCode || addr?.zipCode || '').slice(0, 5),
                addr?.Latitude || '',
                addr?.Longitude || ''
            );
            const data = extractTitleSearchOwners(res);
            setTitleSearchData(data);
        } catch (err) {
            console.error("Failed to check if title contact exists", err);
            setTitleSearchData([]);
        }
    }, [opportunityID, opportunityDetails]);

    const handleTitleSyncSuccess = useCallback(() => {
        setOppRefreshKey((prev) => prev + 1);
        checkTitleContactExist();
    }, [checkTitleContactExist]);

    useEffect(() => {
        if (opportunityID && hasCapability('Show_OneButton')) checkTitleContactExist();
    }, [opportunityID, checkTitleContactExist]);

    const handleOpenBlackKnightLeadModal = async () => {
        if (!opportunityID) return;
        setBlackKnightModalData(null);
        setBlackKnightModalOpen(true);
    };

    const roofLabelById = useMemo(() => {
        const map: Record<string, string> = {};
        (roofCardsFromAiBase || []).forEach((r: any, idx: number) => {
            if (!r?.roofFaceId) return;
            const tag =
                typeof r?.roofNum === 'number' && Number.isFinite(r.roofNum)
                    ? r.roofNum
                    : idx + 1;
            map[r.roofFaceId] = `Roof ${tag}`;
        });
        return map;
    }, [roofCardsFromAiBase]);

    useEffect(() => {
        if (opportunityInitManagedByOrchestrator) return;
        if (opportunityID == null || opportunityID === '' || !hasCapability('Show_OneButton')) {
            setPriceQuoteOverrides(null);
            setPriceQuoteDetails(null);
            setSystemDesignQuotesLoading(false);
            return;
        }
        let cancelled = false;
        setSystemDesignQuotesLoading(true);
        quoteServices
            .getRetrievePriceQuote(String(opportunityID))
            .then((data) => {
                if (cancelled) return;
                const selectedHoverEstimate = useOneButtonStore.getState().selectedHoverEstimate;
                const merged =
                    mergeHoverEstimateIntoPriceQuote(data as any, selectedHoverEstimate) ?? data;
                setPriceQuote(merged);
                setPriceQuoteDetails(merged as any);
                const roof = merged?.RoofSquareOverride;
                const normal = merged?.Normal_Slope_Override;
                const low = merged?.Low_Slope_Override;
                const prev = useOneButtonStore.getState().priceQuoteOverrides;
                const nextOverrides = {
                    ...prev,
                    roofSquareOverride: typeof roof === 'number' && Number.isFinite(roof) ? roof : (prev?.roofSquareOverride ?? null),
                    normalSlopeOverride: typeof normal === 'number' && Number.isFinite(normal) ? normal : (prev?.normalSlopeOverride ?? null),
                    lowSlopeOverride: typeof low === 'number' && Number.isFinite(low) ? low : (prev?.lowSlopeOverride ?? null),
                };
                setPriceQuoteOverrides(
                    mergeHoverEstimateIntoPriceQuoteOverrides(nextOverrides, selectedHoverEstimate),
                );
            })
            .catch(() => {
                if (!cancelled) {
                    setPriceQuoteOverrides(null);
                    setPriceQuoteDetails(null);
                }
            })
            .finally(() => {
                if (!cancelled) setSystemDesignQuotesLoading(false);
            });
        return () => {
            cancelled = true;
            setSystemDesignQuotesLoading(false);
        };
    }, [
        opportunityID,
        quotesRefreshTrigger,
        setPriceQuoteOverrides,
        setPriceQuoteDetails,
        setSystemDesignQuotesLoading,
        opportunityInitManagedByOrchestrator,
    ]);

    const priceQuoteDetails = useOneButtonStore((s) => s.priceQuoteDetails);
    useEffect(() => { 
        setPriceQuote(priceQuoteDetails);  
    }, [priceQuoteDetails]);


    useEffect(() => {
        if (opportunityInitManagedByOrchestrator) return;
        if (opportunityID == null || opportunityID === '') {
            setSystemDesign(null);
            setSystemDesignLosses(null);
            setDesignImage(null);
            return;
        }
        const id = String(opportunityID);
        let cancelled = false;
        Promise.all([
            designServices.RetrieveSystemDesign(id),
            designServices.SystemDesignLosses(id),
            designServices.GetDesignImage(id),
        ]).then(([systemDesign, systemDesignLosses, designImage]) => {
            if (cancelled) return;
            setSystemDesign(systemDesign ?? null);
            setSystemDesignLosses(systemDesignLosses ?? null);
            setDesignImage(designImage ?? null);
        }).catch(() => {
            if (!cancelled) {
                setSystemDesign(null);
                setSystemDesignLosses(null);
                setDesignImage(null);
            }
        });
        return () => { cancelled = true; };
    }, [
        opportunityID,
        systemDesignRefreshTrigger,
        setSystemDesign,
        setSystemDesignLosses,
        setDesignImage,
        opportunityInitManagedByOrchestrator,
    ]);

    const [aiPanelTab, setAiPanelTab] = useState<'ai' | 'saved' | 'tree' | 'wishlist'>('ai');
    const [dashboardSectionPanel, setDashboardSectionPanel] = useState<DashboardSectionPanelId>(() => {
        try {
            if (new URLSearchParams(window.location.search).get('section') === 'financing') {
                return 'financing';
            }
        } catch {
            /* ignore */
        }
        return 'all';
    });
    const [aiCatalogViewMode, setAiCatalogViewMode] = useState<'table' | 'card'>('card');
    const [treeScenariosCatalogViewMode, setTreeScenariosCatalogViewMode] = useState<'table' | 'card'>('card');
    const [savedDesignsViewMode, setSavedDesignsViewMode] = useState<'grid' | 'table'>('grid');
 
    /** Right rail: AI designs + Saved designs (collapsible).
     *  If parent passes aiRailOpen/setAiRailOpen, use those;
     *  otherwise fall back to local state.
     */
    /** Closed by default so the 3D viewport is not covered; open via peek tab or menubar AI. */
    const [aiRailOpenLocal, setAiRailOpenLocal] = useState(false);
    const aiRailOpen = typeof aiRailOpenProp === 'boolean' ? aiRailOpenProp : aiRailOpenLocal;
    const setAiRailOpen = useCallback((next: boolean | ((prev: boolean) => boolean)) => {
        if (setAiRailOpenProp) {
            const current = typeof aiRailOpenProp === 'boolean' ? aiRailOpenProp : aiRailOpenLocal;
            const resolved = typeof next === 'function' ? (next as (prev: boolean) => boolean)(current) : next;
            if (resolved === current) return;
            setAiRailOpenProp(resolved);
            return;
        }
        setAiRailOpenLocal((prev) => {
            const resolved = typeof next === 'function' ? (next as (prev: boolean) => boolean)(prev) : next;
            return resolved === prev ? prev : resolved;
        });
    }, [aiRailOpenProp, aiRailOpenLocal, setAiRailOpenProp]);

    const prevDashboardSectionPanelRef = useRef<DashboardSectionPanelId>('all');
    useEffect(() => {
        if (dashboardSectionPanel === 'designs' && prevDashboardSectionPanelRef.current !== 'designs') {
            setAiPanelTab('ai');
            setAiRailOpen(false);
        }
        prevDashboardSectionPanelRef.current = dashboardSectionPanel;
    }, [dashboardSectionPanel, setAiRailOpen]);

    const designsMenubarSignal = useViewerStore((s) => s.designsMenubarSignal);
    /** Only react when the menubar *increments* the signal — not when other deps change while signal stays > 0. */
    const prevDesignsMenubarSignalRef = useRef(designsMenubarSignal);
    useEffect(() => {
        const sig = designsMenubarSignal;
        if (sig === prevDesignsMenubarSignalRef.current) return;
        prevDesignsMenubarSignalRef.current = sig;
        if (sig === 0) return;
        const tab = useViewerStore.getState().designsMenubarTab;
        setAiPanelTab(tab);
        // Always open the right-side AI designs slide rail (works over dashboard + 3D viewer).
        setAiRailOpen(true);
    }, [designsMenubarSignal, setAiRailOpen, setAiPanelTab]);

    /** AI designs needs production values; start deferred oneDRAW draw + calc chain without leaving the page. */
    useEffect(() => {
        const aiDesignsSurfaceOpen = dashboardSectionPanel === 'designs' || aiRailOpen;
        if (!aiDesignsSurfaceOpen || !loadingCompleted) return;
        if (isSiteViewer3dBlocked()) return;
        const {
            deferredSiteModelLoad,
            siteViewerCalculationsPending: pendingCalcs,
            siteModelLoadPending: pendingModel,
        } = useViewerStore.getState();
        if (!deferredSiteModelLoad && !pendingCalcs && !pendingModel) return;
        void startDeferredSiteProductionCalculations();
    }, [
        dashboardSectionPanel,
        aiRailOpen,
        loadingCompleted,
        sceneViewer,
        siteViewerCalculationsPending,
        siteModelLoadPending,
    ]);

    /** Slide rail vs dashboard: close rail when opening full dashboard; in 3D, close rail unless menubar just opened it. */
    const prevDashboardOpenForRailRef = useRef(isDashboardOpen);
    useEffect(() => {
        const wasOpen = prevDashboardOpenForRailRef.current;
        prevDashboardOpenForRailRef.current = isDashboardOpen;

        if (!wasOpen && isDashboardOpen) {
            setAiRailOpen(false);
            return;
        }

        if (wasOpen && !isDashboardOpen) {
            setAiRailOpen(false);
        }
    }, [isDashboardOpen, setAiRailOpen]);

    const onView3dPeekClick = useCallback(() => {
        openSiteViewer3dFromDashboard(setIsDashboardOpen);
    }, [setIsDashboardOpen]);

    const prevDashboardOpenRef = useRef(isDashboardOpen);
    useEffect(() => {
        if (prevDashboardOpenRef.current && !isDashboardOpen && aiPanelTab === 'saved') {
            setAiPanelTab('ai');
        }
        prevDashboardOpenRef.current = isDashboardOpen;
    }, [isDashboardOpen, aiPanelTab]);

    const [treeCuttingPopupOpen, setTreeCuttingPopupOpen] = useState(false);
    const treeCuttingScenariosModalOpen = useViewerStore(
        (s: any) => Boolean(s.treeCuttingScenariosModalOpen),
    );
    /** When set, tree cutting modal was opened for this layout only (show only this design's scenarios). */
    const [treeCuttingForLayoutKey, setTreeCuttingForLayoutKey] = useState<string | null>(null);
    /** Confirmation to process tree cutting for a design when no data exists yet. */
    const [treeCuttingConfirmOpen, setTreeCuttingConfirmOpen] = useState(false);
    /** Tree-cutting data for the modal: use per-layout cached scenarios so module count matches the scenario data, not the current live design. */
    const liveDefaultModules = typeof modeCount === 'number' && Number.isFinite(modeCount) ? modeCount : undefined;
    const effectiveTreeLayoutKey = treeCuttingForLayoutKey ?? selectedRoofCombinationKey ?? 'default';
    const {
        handleWishlistDesign,
        handleWishlistVariantDesign,
        handleSaveTreeCutScenario,
        handleSaveTreeCutScenarioWithLayoutKey,
        isTreeCutScenarioSaved,
        isTreeCutScenarioSavedForLayout,
    } = useDashboardDesignHandlers({
        savedAIDesigns,
        removeAIDesign,
        roofCombinationsFromAi,
        bestRoofsForLayout,
        layoutProductionByKey,
        layoutModulesByKey,
        treeScenariosByLayoutKey,
        treeCount,
        layoutSnapshotsByKey,
        addAIDesign,
        addTreeCutSubScenario,
        effectiveTreeLayoutKey,
        modulePowerW: (activeModule as { power?: number })?.power ?? 0,
    });
    const { modalTreeData } = useTreeCutModalState({
        effectiveTreeLayoutKey,
        treeScenariosByLayoutKey,
        singleTreeWithWorthIt,
        multiTreeScenarios,
        baseWattage,
        baseMultiplier,
        totalModulesBase,
        layoutModulesByKey,
    });

    const [treeCuttingConfirmForKey, setTreeCuttingConfirmForKey] = useState<string | null>(null);

    type DesignCatalogSubPanelState =
        | null
        | {
              kind: 'tree';
              phase: 'confirm' | 'content';
              layoutKey: string;
              designLabel: string;
              mainSnapshotUrl?: string;
          }
        | {
              kind: 'layouts';
              layoutKey: string;
              categoryLabel: string;
              designLabel: string;
              mainSnapshotUrl?: string;
          };

    const [designCatalogSubPanel, setDesignCatalogSubPanel] = useState<DesignCatalogSubPanelState>(null);

    // Tree Cutting Modal opens ONLY on explicit "Show AI Options" click in the
    // tree context menu. Selecting / right-clicking a tree no longer auto-opens it.
    const treeCuttingAiOptionsRequestId = useViewerStore(
        (s: any) => s.treeCuttingAiOptionsRequestId as number | undefined,
    );
    const lastTreeCuttingAiOptionsRequestIdRef = useRef<number>(0);
    useEffect(() => {
        const id = typeof treeCuttingAiOptionsRequestId === 'number' ? treeCuttingAiOptionsRequestId : 0;
        if (id === 0) return;
        if (id === lastTreeCuttingAiOptionsRequestIdRef.current) return;
        lastTreeCuttingAiOptionsRequestIdRef.current = id;
        if (reRunningTreeScenarios || designCatalogSubPanel) return;
        if (useOneButtonStore.getState().dashboardContactsModalOpen) return;
        setTreeCuttingForLayoutKey(null);
        setTreeCuttingPopupOpen(true);
        useViewerStore.setState({ treeCuttingScenariosModalOpen: true });
    }, [treeCuttingAiOptionsRequestId, reRunningTreeScenarios, designCatalogSubPanel]);

    const [maxPanelsTreeCuttingPopupOpen, setMaxPanelsTreeCuttingPopupOpen] = useState(false);
    const [predictIdealModalOpen, setPredictIdealModalOpen] = useState(false);
    const [allCombinationsModalOpen, setAllCombinationsModalOpen] = useState(false);
    const [roofSuggestionsModalOpen, setRoofSuggestionsModalOpen] = useState(false);
    const [ahjModalOpen, setAhjModalOpen] = useState(false);
    const [oppValidationModalOpen, setOppValidationModalOpen] = useState(false);
    const [pricingGridModalOpen, setPricingGridModalOpen] = useState(false);
    /** When set, inline Financing Options re-runs pricing (finalized design / products-solar $). */
    const [pricingFetchTrigger, setPricingFetchTrigger] = useState<number | null>(null);
    /** Scenario design for modal $ / card context highlighting; not used for inline Financing Options payload. */
    const [pricingDesignForTrigger, setPricingDesignForTrigger] = useState<SelectedDesignForPricing | null>(null);

    /** Row-level pricing API body cache; partner cells load lazily via $ (one API call per row, shared across partner columns). */
    const treeCutRowPricingBodyRef = useRef<Record<string, unknown>>({});
    const treeCutRowPricingInFlightRef = useRef<Record<string, Promise<unknown>>>({});
    const [treeCutPartnerCellState, setTreeCutPartnerCellState] = useState<Record<string, TreeCutPartnerCellState>>({});
    const mainCardPricingBodyRef = useRef<Record<string, unknown>>({});
    const mainCardPricingInFlightRef = useRef<Record<string, Promise<unknown>>>({});
    const [mainCardPartnerCellState, setMainCardPartnerCellState] = useState<Record<string, MainCardPartnerCellState>>({});

    const {
        triggerTreeCutScenarioPartnerPricing,
        renderTreeCutGoodLeapPurchaseOnCard,
        renderTreeCutIgsPrimaryOnCard,
        renderTreeCutLightReachTotalEpcPaymentOnCard,
        getTreeCutPartnerCellState,
        getTreeCutEpcByPartner,
        isTreeCutEpcLoading,
        ensureTreeCutPricingForCard,
        clearTreeCutEpcCache,
    } = useTreeCutScenarioPricing({
        solarUtilityData,
        dashboardAnnualUsage,
        SFannualUsage,
        priceQuoteOverrides,
        treeCutRowPricingBodyRef,
        treeCutRowPricingInFlightRef,
        modalTotalModulesBase:
            typeof modalTreeData.totalModulesBase === 'number' && Number.isFinite(modalTreeData.totalModulesBase)
                ? modalTreeData.totalModulesBase
                : undefined,
        treeCutScenarioBaseWattage:
            typeof modalTreeData.baseWattage === 'number' && Number.isFinite(modalTreeData.baseWattage)
                ? modalTreeData.baseWattage
                : undefined,
        activeModule,
        setTreeCutPartnerCellState,
        treeCutPartnerCellState,
    });

    useEffect(() => {
        if (!treeCuttingPopupOpen) {
            treeCutRowPricingBodyRef.current = {};
            treeCutRowPricingInFlightRef.current = {};
            setTreeCutPartnerCellState({});
            clearTreeCutEpcCache();
        }
    }, [treeCuttingPopupOpen, clearTreeCutEpcCache]);

    const {
        triggerMainCardPartnerPricing,
        renderMainCardPartnerValue,
        ensureMainCardPricingForCard,
        getDesignEpcByPartner,
        getMainCardPartnerCellState,
        isMainCardEpcLoading,
    } = useMainCardScenarioPricing({
        solarUtilityData,
        priceQuoteOverrides,
        mainCardPricingBodyRef,
        mainCardPricingInFlightRef,
        setMainCardPartnerCellState,
        mainCardPartnerCellState,
    });

    const closeTreeCutScenariosPopup = useCallback(() => {
        setTreeCuttingPopupOpen(false);
        setTreeCuttingForLayoutKey(null);
        useViewerStore.setState({ treeCuttingScenariosModalOpen: false });
    }, []);

    const openTreeCutScenariosModal = useCallback(() => {
        setTreeCuttingPopupOpen(true);
        useViewerStore.setState({ treeCuttingScenariosModalOpen: true });
    }, []);

    /** Close tree-cutting UI while contacts modal is open — avoids Tree Calculator loading overlays on top. */
    useEffect(() => {
        if (!dashboardContactsModalOpen) return;
        closeTreeCutScenariosPopup();
        setTreeCuttingConfirmOpen(false);
        setTreeCuttingConfirmForKey(null);
        setDesignCatalogSubPanel((prev) => (prev?.kind === 'tree' ? null : prev));
    }, [dashboardContactsModalOpen, closeTreeCutScenariosPopup]);

    /** Setters from useState below are stable; keep [] so this can sit before alt-layout state. */
    const closeDesignCatalogSubPanel = useCallback(() => {
        setDesignCatalogSubPanel(null);
        setAltLayoutsCategory(null);
        setAltLayoutsLoading(false);
        setTreeCuttingForLayoutKey(null);
    }, []);

    const handleCatalogTreeSubpanelOpen = useCallback(
        (payload: {
            layoutKey: string;
            designLabel: string;
            mainSnapshotUrl?: string;
            needsConfirm: boolean;
        }) => {
            if (payload.needsConfirm) {
                setDesignCatalogSubPanel({
                    kind: 'tree',
                    phase: 'confirm',
                    layoutKey: payload.layoutKey,
                    designLabel: payload.designLabel,
                    mainSnapshotUrl: payload.mainSnapshotUrl,
                });
                return;
            }
            setDesignCatalogSubPanel({
                kind: 'tree',
                phase: 'content',
                layoutKey: payload.layoutKey,
                designLabel: payload.designLabel,
                mainSnapshotUrl: payload.mainSnapshotUrl,
            });
            setSelectedRoofCombinationKey(payload.layoutKey);
            setTreeCuttingForLayoutKey(payload.layoutKey);
        },
        [setSelectedRoofCombinationKey],
    );

    const closeTreeCuttingFromCatalogSubpanel = useCallback(
        (open: boolean) => {
            if (!open) closeDesignCatalogSubPanel();
        },
        [closeDesignCatalogSubPanel],
    );

    const setTreeCuttingPopupOpenWrapped = useCallback(
        (open: boolean) => {
            if (open) openTreeCutScenariosModal();
            else closeTreeCutScenariosPopup();
        },
        [closeTreeCutScenariosPopup, openTreeCutScenariosModal],
    );

    const isTreeCuttingScenariosModalVisible =
        (treeCuttingPopupOpen || treeCuttingScenariosModalOpen) && !designCatalogSubPanel;

    const layoutCandidatesByCategory = useMemo(() => {
        type Candidate = {
            key: string;
            roofFaceIds: string[];
            dir?: 'port' | 'land' | 'both' | null;
            minEff?: number | null;
            production: number;
            modules: number;
            avgSolar: number;
            effPct: number | null;
            multiplier?: number | null;
            variantId?: string;
            label?: string;
            displayLabel?: string;
            modulesLayout?: import('../../../../@types/types').SavedRoofModulesLayout;
        };

        const limitPerCategory = 8;
        const byCategory: Record<string, Candidate[]> = {};
        const variantsByCategory = aiCategoryLayoutVariants as Record<string, unknown[]>;

        for (const category of getLayoutCategoryNames()) {
            let ranked = rankCategoryCandidates(
                category,
                collectCategoryCandidates({
                    category,
                    variantsByCategory,
                    layoutMetricsByKey: layoutMetricsByKey as Record<string, any>,
                    layoutProductionByKey: layoutProductionByKey as Record<string, number>,
                    layoutModulesByKey: layoutModulesByKey as Record<string, number>,
                    layoutSnapshotsByKey: layoutSnapshotsByKey as Record<string, string | undefined>,
                    layoutModulesLayoutByKey: layoutModulesLayoutByKey as Record<string, any>,
                    roofIdsByLayoutKey: getRoofIdsForLayoutKey,
                    categoryMainLayoutByCategory: categoryMainLayoutByCategory as Record<string, any>,
                    roofCombinationsFromAi: roofCombinationsFromAi as Array<{ name: string; roofFaceIds?: string[] }>,
                }),
            );
            if (!ranked.length) {
                const cachedMain = buildCachedMainLayoutCandidate(
                    category,
                    layoutProductionByKey as Record<string, number>,
                    layoutModulesByKey as Record<string, number>,
                    layoutMetricsByKey as Record<string, any>,
                    layoutSnapshotsByKey as Record<string, string | undefined>,
                    categoryMainLayoutByCategory as Record<string, any>,
                    getRoofIdsForLayoutKey,
                    layoutModulesLayoutByKey as Record<string, any>,
                );
                if (cachedMain) ranked = [cachedMain];
            }
            if (!ranked.length) continue;

            const toCandidate = (c: (typeof ranked)[number], keyOverride?: string): Candidate => ({
                key: keyOverride ?? c.key,
                roofFaceIds: c.roofIds,
                dir: c.dir ?? null,
                minEff: c.minEff ?? null,
                production: c.production,
                modules: c.modules,
                avgSolar: c.solarAccess ?? 0,
                effPct: c.efficiency,
                multiplier: c.multiplier,
                variantId: c.variantId,
                label: c.label,
                displayLabel: c.displayLabel,
                modulesLayout: c.modulesLayout,
            });

            const catKey = getCategoryMainLayoutKey(category);
            const mainMeta = (categoryMainLayoutByCategory as Record<string, any>)?.[category];
            const primary = resolveCategoryMainCardCandidate(
                category,
                ranked,
                categoryMainLayoutByCategory as Record<string, import('./utils/categoryBestVariantScoring').CategoryLayoutCandidate>,
            );
            if (!primary) continue;

            const primarySig = candidateLayoutSignature(primary);
            const alternates = ranked.filter((c) => candidateLayoutSignature(c) !== primarySig);
            byCategory[category] = [
                {
                    ...toCandidate(primary, catKey),
                    displayLabel:
                        mainMeta?.displayLabel ??
                        formatCategoryWinnerDisplayLabel(category, { ...primary, label: primary.label }),
                },
                ...alternates.map((c) => toCandidate(c)),
            ].slice(0, limitPerCategory);
        }

        return byCategory;
    }, [
        layoutMetricsByKey,
        layoutProductionByKey,
        layoutModulesByKey,
        aiCategoryLayoutVariants,
        layoutSnapshotsByKey,
        layoutModulesLayoutByKey,
        getRoofIdsForLayoutKey,
        categoryMainLayoutByCategory,
        roofCombinationsFromAi,
    ]);

    /** Keep stored category winners + main-card metric maps aligned with live ranking. */
    useEffect(() => {
        const stored = (categoryMainLayoutByCategory ?? {}) as Record<
            string,
            import('./utils/categoryBestVariantScoring').CategoryLayoutCandidate
        >;
        const nextMain = { ...stored };
        const nextProduction = { ...(layoutProductionByKey as Record<string, number>) };
        const nextModules = { ...(layoutModulesByKey as Record<string, number>) };
        const nextMetrics = { ...(layoutMetricsByKey as Record<string, any>) };
        const nextModulesLayout = { ...(layoutModulesLayoutByKey as Record<string, any>) };
        let changed = false;

        for (const category of Object.keys(layoutCandidatesByCategory)) {
            const live = layoutCandidatesByCategory[category]?.[0];
            if (!live?.roofFaceIds?.length) continue;

            const catKey = getCategoryMainLayoutKey(category);
            const asCandidate: import('./utils/categoryBestVariantScoring').CategoryLayoutCandidate = {
                key: catKey,
                source: live.variantId ? 'variant' : 'layoutKey',
                variantId: live.variantId,
                label: live.label,
                displayLabel:
                    live.displayLabel ??
                    formatCategoryWinnerDisplayLabel(category, {
                        label: live.label,
                        dir: live.dir ?? null,
                    }),
                roofIds: live.roofFaceIds,
                dir: live.dir ?? null,
                minEff: live.minEff ?? null,
                production: live.production,
                modules: live.modules,
                efficiency: live.effPct,
                solarAccess: live.avgSolar,
                multiplier: live.multiplier ?? null,
                modulesLayout: live.modulesLayout,
                roofCount: live.roofFaceIds.length,
            };

            const prev = stored[category];
            const prevSig = prev
                ? candidateLayoutSignature({
                      ...prev,
                      key: catKey,
                      roofIds: (prev.roofIds ?? []).filter(Boolean),
                  })
                : null;
            const liveSig = candidateLayoutSignature(asCandidate);
            const metricsMatch =
                nextProduction[catKey] === asCandidate.production &&
                nextModules[catKey] === asCandidate.modules &&
                nextMetrics[catKey]?.efficiency === asCandidate.efficiency &&
                nextMetrics[catKey]?.solarAccess === asCandidate.solarAccess &&
                nextMetrics[catKey]?.multiplier === asCandidate.multiplier;

            if (prevSig === liveSig && metricsMatch) continue;

            nextMain[category] = asCandidate;
            nextProduction[catKey] = asCandidate.production;
            nextModules[catKey] = asCandidate.modules;
            nextMetrics[catKey] = {
                ...(nextMetrics[catKey] ?? {}),
                efficiency: asCandidate.efficiency,
                solarAccess: asCandidate.solarAccess,
                multiplier: asCandidate.multiplier,
                roofCount: asCandidate.roofCount ?? live.roofFaceIds.length,
            };
            if (asCandidate.modulesLayout?.length) {
                nextModulesLayout[catKey] = asCandidate.modulesLayout;
                nextModulesLayout[category] = asCandidate.modulesLayout;
            }
            changed = true;
        }

        if (!changed) return;
        useViewerStore.setState({
            categoryMainLayoutByCategory: nextMain,
            layoutProductionByKey: nextProduction,
            layoutModulesByKey: nextModules,
            layoutMetricsByKey: nextMetrics,
            layoutModulesLayoutByKey: nextModulesLayout,
        } as any);
    }, [
        layoutCandidatesByCategory,
        categoryMainLayoutByCategory,
        layoutProductionByKey,
        layoutModulesByKey,
        layoutMetricsByKey,
        layoutModulesLayoutByKey,
    ]);

    /** Layout design categories — derived from variant defs so new categories rank automatically. */
    const layoutCategoryNames = useMemo(() => getLayoutCategoryNames(), []);

    /** Design count per category (main layout options from AI). */
    const designCountByCategory = useMemo(() => {
        const counts: Record<string, number> = {};
        layoutCategoryNames.forEach((name) => {
            counts[name] = layoutCandidatesByCategory[name]?.length ?? 0;
        });
        return counts;
    }, [layoutCandidatesByCategory, layoutCategoryNames]);

    /** True when catalogue / slide rail has layout rows to show (not only empty placeholder). */
    const hasAiLayoutCatalog = useMemo(() => {
        if (!aiScenariosSectionVisible) return false;
        const hasCategoryRows = layoutCategoryNames.some(
            (cat) => (layoutCandidatesByCategory[cat]?.length ?? 0) > 0,
        );
        if (hasCategoryRows) return true;
        return hasSimulatedLayoutCatalogData(
            layoutProductionByKey as Record<string, number | undefined>,
            layoutModulesByKey as Record<string, number | undefined>,
        );
    }, [
        aiScenariosSectionVisible,
        layoutCategoryNames,
        layoutCandidatesByCategory,
        layoutProductionByKey,
        layoutModulesByKey,
    ]);

    const [altLayoutsModalOpen, setAltLayoutsModalOpen] = useState(false);
    const [altLayoutsLoading, setAltLayoutsLoading] = useState(false);
    const [altLayoutsCategory, setAltLayoutsCategory] = useState<string | null>(null);
    layoutCandidatesByCategoryRef.current = layoutCandidatesByCategory;

    closeLayoutOptionsUiRef.current = () => {
        setDesignCatalogSubPanel(null);
        setAltLayoutsModalOpen(false);
        setAltLayoutsCategory(null);
        setAltLayoutsLoading(false);
        setTreeCuttingForLayoutKey(null);
        setAiRailOpen(false);
    };

    /** When the slide rail opens in the viewer, load saved designs if catalogue data is still empty. */
    useEffect(() => {
        if (!aiRailOpen || isBatchMode || !hasCapability('Show_AI_Scenarios')) return;
        if (!loadingCompleted || refreshingShadeFromStore) return;
        if (hasAiLayoutCatalog) return;
        if (useOneButtonStore.getState().dashboardContactsModalOpen) return;

        void loadSavedAiDesignsIfNeeded(
            (opportunityDetails?.SalesForce_Id && String(opportunityDetails.SalesForce_Id).trim()) ||
                getSalesforceIdForAiCache() ||
                undefined,
        );
    }, [
        aiRailOpen,
        isBatchMode,
        loadingCompleted,
        refreshingShadeFromStore,
        hasAiLayoutCatalog,
        opportunityDetails?.SalesForce_Id,
        loadSavedAiDesignsIfNeeded,
    ]);

    /** Close layout-options UI once when Run AI starts (viewer mutations must not race the sub-panel). */
    const prevAiLoadingForCatalogResetRef = useRef(false);
    useEffect(() => {
        const wasLoading = prevAiLoadingForCatalogResetRef.current;
        prevAiLoadingForCatalogResetRef.current = aiLoading;
        if (!aiLoading || wasLoading) return;
        setDesignCatalogSubPanel(null);
        setAltLayoutsModalOpen(false);
        setAltLayoutsCategory(null);
        setAltLayoutsLoading(false);
        setAiRailOpen(false);
    }, [aiLoading, setAiRailOpen]);

    /** After Run AI finishes, return to the full dashboard on the AI Designs section. */
    const prevAiLoadingForDashboardReturnRef = useRef(false);
    useEffect(() => {
        const wasLoading = prevAiLoadingForDashboardReturnRef.current;
        prevAiLoadingForDashboardReturnRef.current = aiLoading;
        if (aiLoading) return;
        if (!wasLoading || isBatchMode) return;
        if (!runAiDashboardReturnPendingRef.current) return;
        runAiDashboardReturnPendingRef.current = false;

        setPathname('/configure/modules');
        setDesignCatalogSubPanel(null);
        setAltLayoutsCategory(null);
        setAltLayoutsLoading(false);
        setAltLayoutsModalOpen(false);
        setTreeCuttingPopupOpen(false);
        setTreeCuttingForLayoutKey(null);
        useViewerStore.setState({ treeCuttingScenariosModalOpen: false });
        setAiPanelTab('ai');
        setDashboardSectionPanel('designs');
        setIsDashboardOpen(true);
    }, [
        aiLoading,
        isBatchMode,
        setIsDashboardOpen,
        setDashboardSectionPanel,
        setAiPanelTab,
    ]);

    const handleOpenCategoryLayoutOptions = useCallback(
        async (payload: {
            categoryLabel: string;
            layoutKey: string;
            designLabel?: string;
            mainSnapshotUrl?: string;
            /** When true, show options in the inline catalog sub-panel instead of the modal. */
            useSubpanel?: boolean;
        }) => {
            if ((useViewerStore.getState() as any).aiLoading === true) {
                return;
            }

            const categoryLabel = payload.categoryLabel;
            const variantsByCategory = (useViewerStore.getState() as any).aiCategoryLayoutVariants as
                | Record<string, unknown[]>
                | undefined;
            const rawVariants = resolveVariantsForCategory(categoryLabel, variantsByCategory ?? {});

            setAltLayoutsCategory(categoryLabel);

            if (payload.useSubpanel) {
                setDesignCatalogSubPanel({
                    kind: 'layouts',
                    categoryLabel,
                    layoutKey: payload.layoutKey,
                    designLabel: payload.designLabel ?? categoryLabel,
                    mainSnapshotUrl: payload.mainSnapshotUrl,
                });
            } else {
                setAltLayoutsModalOpen(true);
            }

            if (rawVariants.length > 0) {
                setAltLayoutsLoading(false);
                return;
            }

            const aiRunInProgress = (useViewerStore.getState() as any).aiLoading === true;
            if (aiRunInProgress) {
                setAltLayoutsLoading(false);
                return;
            }

            setAltLayoutsLoading(true);
            await new Promise<void>((resolve) => setTimeout(resolve, 0));

            try {
                await runGenerateCategoryLayoutVariantsAction({
                    sceneViewer,
                    categoryLabel,
                    layoutKey: payload.layoutKey,
                    roofCombinationsFromAi,
                    defaultMaxfillRoofIds,
                    bestRoofsForLayout,
                    layoutCandidatesByCategory: layoutCandidatesByCategoryRef.current,
                    selectedRoofCombinationKey: selectedRoofCombinationKey ?? 'default',
                });
            } catch {
                // Sub-panel / modal shows empty state when generation fails
            } finally {
                setAltLayoutsLoading(false);
            }
        },
        [
            sceneViewer,
            roofCombinationsFromAi,
            defaultMaxfillRoofIds,
            bestRoofsForLayout,
            selectedRoofCombinationKey,
        ],
    );

    const designList = useViewerStore((s) => s.designList) || [];
    const selectedDesignIndex = useViewerStore((s) => s.selectedDesignIndex);
    const selectedSavedDesignLabel = useMemo(() => {
        if (!Array.isArray(designList) || selectedDesignIndex == null) return null;
        const selected = designList[selectedDesignIndex] as any;
        if (!selected) return null;
        const hasDesignNo = selected.Design_No !== undefined && selected.Design_No !== null;
        const hasVersionNo = selected.Version_No !== undefined && selected.Version_No !== null;
        if (!hasDesignNo || !hasVersionNo) return null;
        return `Design ${selected.Design_No} · Version ${selected.Version_No}`;
    }, [designList, selectedDesignIndex]);

    const applyRoofSetInViewer = useCallback(
        async (
            roofFaceIds: string[],
            opts?: {
                dir?: 'port' | 'land' | 'both' | null;
                minEff?: number | null;
                /** When true, the caller runs the single shade pass; skip it here to avoid double shade. */
                deferShadeCalc?: boolean;
            },
        ) => {
            const self = sceneViewer;
            if (!self || !roofFaceIds?.length) return;

            try {
                useViewerStore.setState({ isHideAllGhostTrees: false });
                self.setVisibleGroup?.('tree', true);
                (self as any)?.hideObject?.('GhostTree', false);
            } catch {
                // ignore
            }

            setIsDashboardOpen(false);
            clearAllModuleMeshes(self);
            const dir = opts?.dir ?? null;
            const minEff = normalizeLayoutMinEff(opts?.minEff);
            // Skip the internal shade pre-warm; shade is computed exactly once below (or by the caller).
            addModulesToRoofsByIds(self, roofFaceIds, dir as any, { skipShadePrewarm: true });
            await SetModuleGraph(self);
            if (minEff != null) {
                removeGreyModulesAndSingleModuleRoofs(self, roofFaceIds, minEff);
            } else {
                removeGreyModulesAndSingleModuleRoofs(self, roofFaceIds);
            }
            await SetModuleGraph(self);
            if (!opts?.deferShadeCalc) {
                siteCalculationState.needsUpdate(SHADE_CALCULATIONS);
                await siteCalculationState.useCalculations(SHADE_CALCULATIONS);
            }
        },
        [sceneViewer, setIsDashboardOpen],
    );
    applyRoofSetInViewerRef.current = applyRoofSetInViewer;

    const handleApplyAdHocLayout = useCallback(
        async (roofFaceIds: string[]) => {
            await applyRoofSetInViewer(roofFaceIds);
        },
        [applyRoofSetInViewer],
    );

    const handleSelectLayoutCard = (key: string, details: {
        estProd: number | undefined;
        estModules: number | undefined;
        systemKw: string;
        /** Preferred: raw system size in kW (full precision, not display-rounded). */
        systemSizeKw?: number;
        roofCount: number;
        categoryId: string;
        designId: string;
    }) => {
        // Toggle behavior: clicking the currently selected design unselects it.
        if (selectedDesignForPricing?.designId === details.designId) {
            setSelectedDesignForPricing(null);
            return;
        }
        setSelectedRoofCombinationKey(key);
        const systemSizeKw =
            (typeof details.systemSizeKw === 'number' && Number.isFinite(details.systemSizeKw) ? details.systemSizeKw : null) ??
            (parseFloat(details.systemKw) || 0);
        /** `estProd` from layout cards is annual kWh/yr (see design card "kWh/yr"), not watts. */
        const layoutAnnualKwh = details.estProd;
        const estimatedAnnualProductionKwh =
            layoutAnnualKwh != null && Number.isFinite(layoutAnnualKwh) ? Math.round(layoutAnnualKwh) : 0;
        const design: SelectedDesignForPricing = {
            designId: details.designId,
            categoryId: details.categoryId,
            systemSizeKw,
            estimatedAnnualProductionKwh,
            productionW: layoutAnnualKwh,
            modules: details.estModules,
            roofCount: details.roofCount,
            layoutKey: key,
        };
        setSelectedDesignForPricing(design);
    };

    const triggerPricingGridForDesign = useCallback(
        (
            design: SelectedDesignForPricing,
            openModalOrOptions:
                | boolean
                | { openModal?: boolean; syncPanelSelection?: boolean; refreshInlineGrid?: boolean } = true,
        ) => {
            let openModal = true;
            let syncPanelSelection = true;
            let refreshInlineGrid = true;
            if (typeof openModalOrOptions === 'boolean') {
                openModal = openModalOrOptions;
            } else if (openModalOrOptions && typeof openModalOrOptions === 'object') {
                openModal = openModalOrOptions.openModal ?? true;
                syncPanelSelection = openModalOrOptions.syncPanelSelection ?? true;
                refreshInlineGrid = openModalOrOptions.refreshInlineGrid ?? true;
            }
            if (syncPanelSelection) {
                setSelectedDesignForPricing(design);
            }
            setPricingDesignForTrigger(design);
            if (openModal) setPricingGridModalOpen(true);
            const ts = Date.now();
            if (refreshInlineGrid) {
                setPricingFetchTrigger(ts);
            } else {
                setPricingModalFetchTrigger(ts);
            }
        },
        [setSelectedDesignForPricing, setPricingModalFetchTrigger],
    );

    /** Called when user clicks the $ button: open financing for this layout’s metrics without changing AI panel selection or active roof layout. */
    const opportunityNumericId = useMemo(
        () => opportunityID ?? opportunityDetails?.Opportunity_Id ?? opportunityDetails?.OpportunityId ?? null,
        [opportunityID, opportunityDetails],
    );

    const commerceHubLoading = !designAndShadeReady && !hasPrimaryDesign && !hasSiteDesignDetails;

    const onCommerceHubSolarPricingClick = useCallback(() => {
        if (selectedDesignForPricing) {
            triggerPricingGridForDesign(selectedDesignForPricing);
            return;
        }
        setPricingGridModalOpen(true);
        setPricingFetchTrigger(Date.now());
    }, [
        selectedDesignForPricing,
        triggerPricingGridForDesign,
        setPricingGridModalOpen,
        setPricingFetchTrigger,
    ]);

    const handlePricingDollarClick = (key: string, details: {
        estProd: number | undefined;
        estModules: number | undefined;
        systemKw: string;
        /** Preferred: raw system size in kW (full precision, not display-rounded). */
        systemSizeKw?: number;
        roofCount: number;
        categoryId: string;
        designId: string;
    }) => {
        const systemSizeKw =
            (typeof details.systemSizeKw === 'number' && Number.isFinite(details.systemSizeKw) ? details.systemSizeKw : null) ??
            (parseFloat(details.systemKw) || 0);
        const layoutAnnualKwh = details.estProd;
        const estimatedAnnualProductionKwh =
            layoutAnnualKwh != null && Number.isFinite(layoutAnnualKwh) ? Math.round(layoutAnnualKwh) : 0;
        const design: SelectedDesignForPricing = {
            designId: details.designId,
            categoryId: details.categoryId,
            systemSizeKw,
            estimatedAnnualProductionKwh,
            productionW: layoutAnnualKwh,
            modules: details.estModules,
            roofCount: details.roofCount,
            layoutKey: key,
        };
        triggerPricingGridForDesign(design, { syncPanelSelection: false, refreshInlineGrid: false });
    };

    /** Tree row $: open the same financing pricing modal as design cards (partner cells still load inline). */
    const handleTreeScenarioFinancingPricingClick = useCallback(
        (
            scenario: { actualWattage?: number; treeId?: string; treeLabel?: string; treeIds?: string[] },
            table: TreeCutPricingTable,
            scenarioRowIndex: number,
            metrics: { systemKw: string; systemSizeKw?: number; estModules: number | undefined; roofCount: number },
        ) => {
            if (!solarUtilityData) return;
            let layoutAnnualKwh = annualKwhEstimateForTreeCutFinancingModal({
                solarUtilityData,
                dashboardAnnualUsage,
                SFannualUsage,
                treeCutScenarioBaseWattage:
                    typeof modalTreeData.baseWattage === 'number' && Number.isFinite(modalTreeData.baseWattage)
                        ? modalTreeData.baseWattage
                        : undefined,
                scenarioDcWatts: scenario.actualWattage,
            });
            const baseW =
                typeof modalTreeData.baseWattage === 'number' && Number.isFinite(modalTreeData.baseWattage)
                    ? modalTreeData.baseWattage
                    : null;
            const scenW =
                typeof scenario.actualWattage === 'number' && Number.isFinite(scenario.actualWattage)
                    ? scenario.actualWattage
                    : null;
            if (
                (!layoutAnnualKwh || layoutAnnualKwh <= 0) &&
                baseW != null &&
                baseW > 0 &&
                scenW != null &&
                scenW > 0
            ) {
                const layoutSimKwh = layoutProductionByKey?.[effectiveTreeLayoutKey];
                if (typeof layoutSimKwh === 'number' && Number.isFinite(layoutSimKwh) && layoutSimKwh > 0) {
                    const ratio = Math.min(2, Math.max(0.25, scenW / baseW));
                    layoutAnnualKwh = Math.round(layoutSimKwh * ratio);
                }
            }
            const systemSizeKw =
                (typeof metrics.systemSizeKw === 'number' && Number.isFinite(metrics.systemSizeKw) ? metrics.systemSizeKw : null) ??
                (parseFloat(metrics.systemKw) || 0);
            const layoutCardKey = `tree-cut:${effectiveTreeLayoutKey}:${table}:${scenarioRowIndex}`;
            const designId = getProposalIdForCard(layoutCardKey);
            const idsRaw = Array.isArray(scenario.treeIds) && scenario.treeIds.length > 0
                ? scenario.treeIds
                : (scenario.treeId ? [scenario.treeId] : []);
            const treeDetails = idsRaw.map((id) => {
                const tree = treeArr?.find((t: any) => String(t?.userData?.treeId ?? t?.treeId ?? '').trim() === String(id).trim());
                const cartInfo = tree?.userData?.treeCartInfo;
                const getHeightRange = (height: number) => {
                    if (height <= 9) return '0 - 9'; 
                    if (height >= 10 && height <= 30) return '10 - 30';
                    if (height >= 31 && height <= 50) return '31 - 50';
                    if (height >= 51)  return '51+';
                }
                return {
                    treeId: String(id),
                    label: String(tree?.name || scenario.treeLabel || `Tree ${id}`),
                    dbh: cartInfo?.dbh ?? '',
                    height: tree?.userData?.height ? getHeightRange(tree?.userData?.height) : '',
                    stump: cartInfo?.stump ?? '',
                    type: tree?.userData.type ?? '',
                };
            });
            const design: SelectedDesignForPricing = {
                designId,
                categoryId: 'Tree cutting scenario',
                systemSizeKw,
                estimatedAnnualProductionKwh: layoutAnnualKwh,
                productionW: layoutAnnualKwh,
                modules: metrics.estModules,
                roofCount: metrics.roofCount,
                layoutKey: layoutCardKey,
                treeDetails: treeDetails.length > 0 ? treeDetails : undefined,
            };
            triggerPricingGridForDesign(design, { syncPanelSelection: false, refreshInlineGrid: false });
        },
        [
            solarUtilityData,
            dashboardAnnualUsage,
            SFannualUsage,
            modalTreeData.baseWattage,
            effectiveTreeLayoutKey,
            layoutProductionByKey,
            triggerPricingGridForDesign,
            treeArr,
        ],
    );

    /** Load financing pricing for a saved AI design. */
    const handlePricingForSavedDesign = (design: SavedAIDesign) => {
        setSelectedRoofCombinationKey(design.layoutKey);
        setActiveAIDesignId(design.aiDesignId);
        const layoutAnnualKwh = design.productionW;
        const estimatedAnnualProductionKwh =
            layoutAnnualKwh != null && Number.isFinite(layoutAnnualKwh) ? Math.round(layoutAnnualKwh) : 0;
        const pricingDesign: SelectedDesignForPricing = {
            designId: design.aiDesignId,
            categoryId: design.categoryId || design.label,
            systemSizeKw: design.systemKw,
            estimatedAnnualProductionKwh,
            productionW: layoutAnnualKwh,
            modules: design.modules,
            roofCount: design.roofCount,
            layoutKey: design.layoutKey,
        };
        triggerPricingGridForDesign(pricingDesign);
    };

    /** Open Max Panels + Tree cutting modal; if no scenarios yet, run production (shade weights + tree multipliers + tree cutting) first, then open. Uses sceneViewer for snapshots. */
    const handleMaxPanelsTreeCuttingCardClick = () => {
        if (best5MaxPanelsTreeCuttingDesigns.length > 0) {
            setMaxPanelsTreeCuttingPopupOpen(true);
            return;
        }
        if (runAiReady && typeof handleRunAi === 'function') {
            handleRunAi().then(() => setMaxPanelsTreeCuttingPopupOpen(true)).catch(() => {});
            return;
        }
        setMaxPanelsTreeCuttingPopupOpen(true);
    };

    const globalLayoutVariantsReady = useCallback((existing: unknown[] | undefined) => {
        if (!Array.isArray(existing) || existing.length === 0) return false;
        return existing.some((v: any) => {
            const modules = typeof v?.modules === 'number' && Number.isFinite(v.modules) && v.modules > 0;
            const production =
                typeof v?.productionW === 'number' && Number.isFinite(v.productionW) && v.productionW > 0;
            return modules || production;
        });
    }, []);

    /**
     * Populate cross-roof global layout variants when missing (shared by dashboard modal + AI slide rail).
     */
    const ensureGlobalLayoutVariants = useCallback(async () => {
        const existing = (useViewerStore.getState() as any)
            .aiModuleFillingLayoutVariants as any[] | undefined;
        if (globalLayoutVariantsReady(existing)) {
            setAltLayoutsLoading(false);
            return;
        }
        const aiRunInProgress = (useViewerStore.getState() as any).aiLoading === true;
        if (aiRunInProgress) {
            setAltLayoutsLoading(false);
            return;
        }
        setAltLayoutsLoading(true);
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
        try {
            await runGenerateLayoutVariantsAction({
                sceneViewer,
                defaultMaxfillRoofIds,
                bestRoofsForLayout,
                restoreDefaultLayoutFromSnapshot,
                setAiLoadingStep,
                layoutVariantOptions: {
                    includeGlobalAllRoofs: true,
                    includeGlobalBestSubsets: true,
                    includeCategoryLayoutCards: false,
                },
            });
        } catch {
            // ignore generation errors; UI will show its empty state
        } finally {
            setAltLayoutsLoading(false);
        }
    }, [
        globalLayoutVariantsReady,
        sceneViewer,
        defaultMaxfillRoofIds,
        bestRoofsForLayout,
        restoreDefaultLayoutFromSnapshot,
        setAiLoadingStep,
    ]);

    /**
     * Open the global "Other layout options / More layouts" modal. When no
     * cross-roof global variants exist yet (e.g. AI ran with a reduced payload
     * selection that skipped them), generate them inline so the modal always
     * shows content instead of an indefinite loading skeleton.
     */
    const handleOpenGlobalLayoutVariants = useCallback(async () => {
        setAltLayoutsCategory(null);
        const existing = (useViewerStore.getState() as any)
            .aiModuleFillingLayoutVariants as any[] | undefined;
        if (globalLayoutVariantsReady(existing)) {
            setAltLayoutsLoading(false);
            setAltLayoutsModalOpen(true);
            return;
        }
        const aiRunInProgress = (useViewerStore.getState() as any).aiLoading === true;
        if (aiRunInProgress) {
            setAltLayoutsLoading(false);
            return;
        }
        setAltLayoutsModalOpen(true);
        await ensureGlobalLayoutVariants();
    }, [ensureGlobalLayoutVariants, globalLayoutVariantsReady]);

    const setbackNotes = useViewerStore((s) => ((s as any).setbackNotes as string[]) || []);
    const ahjTown = useViewerStore((s) => ((s as any).aHJ_Township as string) || '');


    /**
     * Apply the chosen AI / catalog layout to the live viewer (modules, roofs, trees, shade, irradiance).
     * Historically only `selectedRoofCombinationKey` was updated; nothing in ThreeDViewer subscribes to it,
     * so the viewport must be updated here — same pipeline as `runUseThisLayoutAction` (scenario 0 = ideal).
     */
    const syncCatalogPanelSelectionForLayout = useCallback(
        (layoutKey: string, categoryLabel?: string) => {
            let categoryId =
                layoutKey === 'default' ? 'oneDRAW Default Maxfill' : categoryLabel?.trim() || '';
            if (!categoryId) {
                for (const cat of layoutCategoryNames) {
                    const candidates = layoutCandidatesByCategory[cat];
                    if (
                        Array.isArray(candidates) &&
                        candidates.some(
                            (c: { value?: string; layoutKey?: string }) =>
                                c?.value === layoutKey || c?.layoutKey === layoutKey,
                        )
                    ) {
                        categoryId = cat;
                        break;
                    }
                }
            }
            if (!categoryId) categoryId = layoutKey;
            const designId = `${categoryId}-${layoutKey}`;
            const estProd = layoutProductionByKey[layoutKey];
            const estModules = layoutModulesByKey[layoutKey];
            const modulePowerW = (activeModule as { power?: number })?.power ?? 0;
            const systemSizeKw =
                estModules != null && Number.isFinite(estModules) && modulePowerW > 0
                    ? (estModules * modulePowerW) / 1000
                    : 0;
            const roofCount = resolveLayoutRoofCount({
                layoutKey,
                layoutMetricsByKey: layoutMetricsByKey as Record<string, { roofCount?: number | null }>,
                defaultMaxfillRoofIds,
                roofCards: roofCardsFromAiBase,
                roofCombinationsFromAi,
                categoryMainLayoutByCategory,
                layoutRoofSummariesByKey,
            });
            const layoutAnnualKwh = estProd;
            const estimatedAnnualProductionKwh =
                layoutAnnualKwh != null && Number.isFinite(layoutAnnualKwh) ? Math.round(layoutAnnualKwh) : 0;
            setSelectedDesignForPricing({
                designId,
                categoryId,
                systemSizeKw,
                estimatedAnnualProductionKwh,
                productionW: layoutAnnualKwh,
                modules: estModules,
                roofCount,
                layoutKey,
            });
            setActiveAIDesignId(null);
            setActiveSubScenarioId(null);
            setAiPanelTab('ai');
        },
        [
            layoutCategoryNames,
            layoutCandidatesByCategory,
            layoutProductionByKey,
            layoutModulesByKey,
            activeModule,
            layoutMetricsByKey,
            defaultMaxfillRoofIds,
            roofCardsFromAiBase,
            roofCombinationsFromAi,
            categoryMainLayoutByCategory,
            layoutRoofSummariesByKey,
            setSelectedDesignForPricing,
            setActiveAIDesignId,
            setActiveSubScenarioId,
            setAiPanelTab,
        ],
    );

    const handleApplyLayoutClick = useCallback(
        async (
            layoutKey: string,
            categoryLabel?: string,
            options?: { syncCatalogPanelSelection?: boolean },
        ) => {
            setIsDashboardOpen(false);
            useViewerStore.setState({ isAiGeneratedDesign: true });

            const self = (sceneViewerProp ?? (useViewerStore.getState() as any).sceneViewer) as any;
            if (!self) return;

            const resolvedCategoryLabel = resolveApplyCategoryLabel(layoutKey, categoryLabel);
            const scenarioLookupKey = resolvedCategoryLabel || layoutKey;
            const selectionKey =
                layoutKey === 'default' ? 'default' : resolvedCategoryLabel || layoutKey;

            if (options?.syncCatalogPanelSelection !== false) {
                syncCatalogPanelSelectionForLayout(layoutKey, resolvedCategoryLabel);
            }
            setSelectedRoofCombinationKey(selectionKey);

            logAiDesignOpenStart({
                source: 'catalog/handleApplyLayoutClick',
                layoutKey,
                categoryLabel: resolvedCategoryLabel,
                selectionKey,
            });

            // Exact placement we expect on the roof — used for post-apply verification + auto-correct.
            let appliedModulesLayout: import('../../../../@types/types').SavedRoofModulesLayout | undefined;

            try {
                useViewerStore.setState({ isHideAllGhostTrees: false });
                self.setVisibleGroup?.(LAYER_OPTIONS.MODEL.TREE, true);
                (self as any)?.hideObject?.(HideObjectType.GhostTree, false);
            } catch {
                // ignore
            }

            beginTreeScenarioLayoutSuppress();
            clearPendingUnghostAlert();
            try {
                self?.removeViewerAlert?.();
            } catch {
                // ignore
            }

            try {
                logAiDesignResolveInputs(resolvedCategoryLabel);
                appliedModulesLayout = await applyCategoryLayoutInViewer({
                    viewer: self,
                    layoutKey,
                    categoryLabel: resolvedCategoryLabel,
                    categoryMainLayoutByCategory: (useViewerStore.getState() as any)
                        .categoryMainLayoutByCategory,
                    layoutCandidatesByCategory,
                    variantsByCategory: (useViewerStore.getState() as any).aiCategoryLayoutVariants,
                    layoutModulesLayoutByKey: (useViewerStore.getState() as any).layoutModulesLayoutByKey,
                    roofCombinationsFromAi,
                    bestRoofsForLayout,
                    defaultMaxfillRoofIds,
                    simulateLayoutForRoofIds,
                    applyRoofSetInViewer,
                });
            } catch (layoutApplyError) {
                console.error('applyCategoryLayoutInViewer failed', layoutApplyError);
            }

            try {
                await waitForSceneIdle();

                let scenariosForLayout =
                    treeScenariosByLayoutKey?.[scenarioLookupKey] ??
                    treeScenariosByLayoutKey?.[layoutKey];
                if (!Array.isArray(scenariosForLayout) || scenariosForLayout.length === 0) {
                    const designForLayout = (savedAIDesigns || []).find(
                        (d: any) =>
                            d.layoutKey === layoutKey ||
                            d.layoutKey === scenarioLookupKey ||
                            d.categoryId === resolvedCategoryLabel,
                    );
                    if (designForLayout?.treeScenarios?.length) {
                        scenariosForLayout = designForLayout.treeScenarios;
                    }
                }
                if (Array.isArray(scenariosForLayout) && scenariosForLayout.length > 0) {
                    useViewerStore.setState({
                        aiModuleFillingScenarios: scenariosForLayout,
                        aiModuleFillingDesignId: scenarioLookupKey,
                    } as any);
                    await applyScenarioLayout(0, self);
                } else {
                    restoreAllTrees(self);
                    await SetModuleGraph(self);
                    SetDisplayShadePoints(self, undefined);
                }
            } catch (e) {
                console.error('handleApplyLayoutClick failed', e);
            } finally {
                endTreeScenarioLayoutSuppress();
                try {
                    siteCalculationState.needsUpdate(SHADE_CALCULATIONS);
                    await siteCalculationState.useCalculations(SHADE_CALCULATIONS);

                    let check = verifyAppliedDesign({
                        layoutKey,
                        categoryLabel: resolvedCategoryLabel,
                        expectedModulesLayout: appliedModulesLayout,
                    });
                    if (
                        check.mismatch &&
                        appliedModulesLayout?.length &&
                        isSnapshotApplicableToViewer(self, layoutKey, resolvedCategoryLabel, appliedModulesLayout)
                    ) {
                        console.warn(
                            '[AI-DESIGN-FLOW] auto-correcting viewer from exact placement snapshot',
                        );
                        await restoreModulesLayoutFromSnapshot(self, appliedModulesLayout, {
                            skipShadeRefresh: true,
                        });
                        await waitForSceneIdle();
                        await awaitAuthoritativeModuleGraph(self);
                        check = verifyAppliedDesign({
                            layoutKey,
                            categoryLabel: resolvedCategoryLabel,
                            expectedModulesLayout: appliedModulesLayout,
                        });
                        if (
                            check.mismatch &&
                            appliedModulesLayout?.length &&
                            isSnapshotApplicableToViewer(self, layoutKey, categoryLabel, appliedModulesLayout)
                        ) {
                            console.warn(
                                '[AI-DESIGN-FLOW] auto-correcting viewer from exact placement snapshot',
                            );
                            await restoreModulesLayoutFromSnapshot(self, appliedModulesLayout, {
                                skipShadeRefresh: true,
                            });
                            await waitForSceneIdle();
                            siteCalculationState.needsUpdate(SHADE_CALCULATIONS);
                            await siteCalculationState.useCalculations(SHADE_CALCULATIONS);
                            check = verifyAppliedDesign({
                                layoutKey,
                                categoryLabel,
                                expectedModulesLayout: appliedModulesLayout,
                            });
                        }
                        if (!check.mismatch) {
                            syncActiveAiDesignProductionFromCard();
                            verifyAppliedDesign({
                                layoutKey,
                                categoryLabel,
                                expectedModulesLayout: appliedModulesLayout,
                            });
                        }
                    }
                } catch {
                    // ignore shade flush errors
                }
                logAiDesignOpenEnd();
            }

        },
        [
            sceneViewerProp,
            roofCombinationsFromAi,
            layoutCandidatesByCategory,
            categoryMainLayoutByCategory,
            bestRoofsForLayout,
            defaultMaxfillRoofIds,
            treeScenariosByLayoutKey,
            savedAIDesigns,
            setSelectedRoofCombinationKey,
            setIsDashboardOpen,
            waitForSceneIdle,
            applyRoofSetInViewer,
            simulateLayoutForRoofIds,
            syncCatalogPanelSelectionForLayout,
        ],
    );

    const handleSlideTreeScenarioApplyInViewer = useCallback(
        (scenarioIndex: number) => {
            useViewerStore.setState({ isAiGeneratedDesign: true });
            void handleUseThisLayout(
                scenarioIndex,
                treeCuttingForLayoutKey,
                treeScenariosByLayoutKey?.[effectiveTreeLayoutKey],
            );
            closeTreeCutScenariosPopup();
            setIsDashboardOpen(false);
        },
        [
            handleUseThisLayout,
            treeCuttingForLayoutKey,
            treeScenariosByLayoutKey,
            effectiveTreeLayoutKey,
            closeTreeCutScenariosPopup,
            setIsDashboardOpen,
        ],
    );

    const showBlockingAiError =
        aiError != null && !hasAiLayoutCatalog && !aiLoading;

    const showAiPayloadReductionForm =
        showBlockingAiError &&
        isAiPayloadSizeExceededError(aiError) &&
        aiModuleFillingScenarios.length > 0 &&
        !aiLoading;

    const showCatalogViewToggle = useMemo(
        () => (roofCards.length > 0 || layoutCategoryNames.length > 0) && !designCatalogSubPanel,
        [roofCards.length, layoutCategoryNames.length, designCatalogSubPanel],
    );

    const aiRailMainContent = (
                    <>

                {aiPanelTab === 'ai' && !hasAiLayoutCatalog && !aiScenariosSectionVisible && (
                    <DashboardAiRailAiEmptyState
                        handleRunAi={handleRunAi}
                        aiLoading={aiLoading}
                        runAiReady={runAiReady}
                        runAiBlockedStatus={runAiBlockedStatus}
                    />
                )}
                {aiPanelTab === 'ai' && aiScenariosSectionVisible && (
                <>

                {/* ----- AI scenarios block: Save actions only + content ----- */}
                <div
                    className={`editor-ai-scenarios-block obd-ai-panel-surface obd-theme-${theme}${
                        !hasAiLayoutCatalog ? ' editor-ai-scenarios-block--no-ai-catalog' : ''
                    }`}
                >
                        <DashboardAiScenariosHeader
                            theme={theme}
                            resultCardMinimized={resultCardMinimized}
                            aiLoading={aiLoading}
                        />
                        {!resultCardMinimized && (
                            <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', flex: 1 }}>
                                {aiPanelTab === 'ai' && (
                                    <div className="editor-ai-module-bar-mount" style={{ flexShrink: 0, minWidth: 0 }}>
                                        <DashboardAiScenariosModuleBar
                                            theme={theme}
                                            moduleList={moduleList}
                                            activeModule={activeModule}
                                            onModuleChange={onModuleChange}
                                            sceneViewer={sceneViewer}
                                            handleRunAi={handleRunAi}
                                            aiLoading={aiLoading}
                                            runAiReady={runAiReady}
                                            runAiBlockedStatus={runAiBlockedStatus}
                                            roofCards={roofCards}
                                            handleSaveAiScenarios={handleSaveAiScenarios}
                                            savingScenarios={savingScenarios}
                                            aiSavingProgress={aiSavingProgress}
                                            aiRunElapsedMs={aiRunElapsedMs}
                                            setAltLayoutsCategory={setAltLayoutsCategory}
                                            setAltLayoutsModalOpen={setAltLayoutsModalOpen}
                                            setRoofSuggestionsModalOpen={setRoofSuggestionsModalOpen}
                                            geminiResultsEnabled={
                                                roofCombinationsFromAi.length > 0 || hasAiLayoutCatalog
                                            }
                                            onMoreLayoutsClick={handleOpenGlobalLayoutVariants}
                                            saveDesignsDisabled={showBlockingAiError}
                                            catalogViewMode={aiCatalogViewMode}
                                            onCatalogViewModeChange={setAiCatalogViewMode}
                                            showCatalogViewToggle={showCatalogViewToggle}
                                            aiDesignsDataSource={aiDesignsDataSource}
                                            isLoadingSnapshots={isLoadingSnapshots}
                                        />
                                    </div>
                                )}
                                {showBlockingAiError ? (
                                    <div
                                        className={`editor-ai-error-panel${showAiPayloadReductionForm ? ' editor-ai-error-panel--with-payload' : ''}`}
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            ...(showAiPayloadReductionForm
                                                ? { flex: 1, minHeight: 0, overflow: 'hidden' }
                                                : { flexShrink: 0, overflow: 'auto' }),
                                            borderTop:
                                                theme === 'dark'
                                                    ? '1px solid rgba(148,163,184,0.22)'
                                                    : '1px solid #e2e8f0',
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                alignItems: 'flex-start',
                                                gap: 12,
                                                padding: 16,
                                                flexShrink: 0,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    flex: '1 1 180px',
                                                    minWidth: 0,
                                                    color: 'var(--danger, #ff4d4f)',
                                                    fontSize: 13,
                                                }}
                                            >
                                                {aiError}
                                            </div>
                                            <Space wrap size="small" style={{ flexShrink: 0, marginLeft: 'auto' }}>
                                                {isAiPayloadSizeExceededError(aiError) &&
                                                aiModuleFillingScenarios.length > 0 &&
                                                !aiLoading ? (
                                                    <Button
                                                        type="primary"
                                                        size="small"
                                                        icon={<LightningIcon size={14} weight="fill" aria-hidden />}
                                                        loading={aiLoading}
                                                        disabled={
                                                            !runAiReady ||
                                                            aiLoading ||
                                                            roofCards.length === 0 ||
                                                            isAiRunPayloadSelectionContinueDisabled(
                                                                aiPayloadRetrySelection,
                                                            )
                                                        }
                                                        onClick={() =>
                                                            void handleRetryAiWithPayloadSelection(
                                                                aiPayloadRetrySelection,
                                                            )
                                                        }
                                                    >
                                                        Retry with this selection
                                                    </Button>
                                                ) : null}
                                                <Button
                                                    size="small"
                                                    type="default"
                                                    onClick={() => setAiError(null)}
                                                    aria-label="Dismiss error"
                                                >
                                                    Dismiss error
                                                </Button>
                                            </Space>
                                        </div>
                                        {showAiPayloadReductionForm ? (
                                            <div
                                                className={`obd-ai-run-payload-inline obd-theme-${theme}`}
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    flex: 1,
                                                    minHeight: 0,
                                                    minWidth: 0,
                                                    overflow: 'hidden',
                                                    padding: '0 16px 16px',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        marginTop: 4,
                                                        marginBottom: 10,
                                                        fontWeight: 600,
                                                        fontSize: 14,
                                                        flexShrink: 0,
                                                        color: theme === 'dark' ? 'rgba(248,250,252,0.95)' : '#1e293b',
                                                    }}
                                                >
                                                    Choose what to send to AI
                                                </div>
                                                <div className="obd-ai-run-payload-form-mount">
                                                    <DashboardAiRunPayloadForm
                                                        key={`ai-payload-retry-${String(aiError).slice(0, 64)}-${aiModuleFillingScenarios.length}`}
                                                        theme={theme}
                                                        scenarios={aiModuleFillingScenarios}
                                                        showFooterActions={false}
                                                        onSelectionChange={handleAiPayloadRetrySelectionChange}
                                                        onContinue={(sel) => void handleRetryAiWithPayloadSelection(sel)}
                                                        continueLabel="Retry with this selection"
                                                    />
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                                {!showBlockingAiError ? (
                                        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                        <div
                                            className={`editor-ai-layout-options-scroll${
                                                aiCatalogViewMode === 'table' && !designCatalogSubPanel
                                                    ? ' editor-ai-layout-options-scroll--table-mode'
                                                    : aiCatalogViewMode === 'card' && !designCatalogSubPanel
                                                      ? ' editor-ai-layout-options-scroll--card-mode'
                                                      : ''
                                            }`}
                                        >
                                            {!hasAiLayoutCatalog ? (
                                                <DashboardAiRailAiEmptyState
                                                    handleRunAi={handleRunAi}
                                                    aiLoading={aiLoading}
                                                    runAiReady={runAiReady}
                                                    runAiBlockedStatus={runAiBlockedStatus}
                                                    selectedSavedDesignLabel={selectedSavedDesignLabel}
                                                    embedded
                                                />
                                            ) : roofCards.length > 0 || layoutCategoryNames.length > 0 ? (
                                                <div
                                                    className={
                                                        aiCatalogViewMode === 'table' && !designCatalogSubPanel
                                                            ? 'editor-ai-layout-options-stack--table-mode'
                                                            : aiCatalogViewMode === 'card' && !designCatalogSubPanel
                                                              ? 'editor-ai-layout-options-stack--card-mode'
                                                              : undefined
                                                    }
                                                    style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
                                                >
                                                    {designCatalogSubPanel ? (
                                                        <div
                                                            className={`obd-ai-design-catalog-subpanel obd-theme-${theme}${
                                                                designCatalogSubPanel.kind === 'tree' &&
                                                                designCatalogSubPanel.phase === 'content'
                                                                    ? ' obd-ai-design-catalog-subpanel--tree-cutting-content'
                                                                    : ''
                                                            }`}
                                                        >
                                                            <div className="obd-ai-design-catalog-subpanel__bar">
                                                                <Button
                                                                    type="text"
                                                                    icon={<ArrowLeftOutlined />}
                                                                    className="obd-ai-design-catalog-subpanel__back"
                                                                    onClick={closeDesignCatalogSubPanel}
                                                                >
                                                                    Back to designs
                                                                </Button>
                                                                <div className="obd-ai-design-catalog-subpanel__bar-hero">
                                                                    {designCatalogSubPanel.mainSnapshotUrl ? (
                                                                        <img
                                                                            src={designCatalogSubPanel.mainSnapshotUrl}
                                                                            alt=""
                                                                            className="obd-ai-design-catalog-subpanel__hero-thumb"
                                                                        />
                                                                    ) : (
                                                                        <div className="obd-ai-design-catalog-subpanel__hero-placeholder" />
                                                                    )}
                                                                    <div className="obd-ai-design-catalog-subpanel__hero-text">
                                                                        <div className="obd-ai-design-catalog-subpanel__title">
                                                                            {designCatalogSubPanel.kind === 'layouts' ? (
                                                                                `More options · ${designCatalogSubPanel.categoryLabel}`
                                                                            ) : designCatalogSubPanel.kind === 'tree' &&
                                                                              designCatalogSubPanel.phase === 'content' ? (
                                                                                <span className="obd-ai-design-catalog-subpanel__title-tree">
                                                                                    <TreeEvergreenIcon
                                                                                        size={16}
                                                                                        weight="duotone"
                                                                                        aria-hidden
                                                                                    />
                                                                                    Tree cutting
                                                                                    {designCatalogSubPanel.designLabel ? (
                                                                                        <span className="obd-ai-design-catalog-subpanel__title-design">
                                                                                            {designCatalogSubPanel.designLabel}
                                                                                        </span>
                                                                                    ) : null}
                                                                                </span>
                                                                            ) : (
                                                                                designCatalogSubPanel.designLabel
                                                                            )}
                                                                        </div>
                                                                        {designCatalogSubPanel.kind === 'tree' &&
                                                                        designCatalogSubPanel.phase === 'confirm' ? (
                                                                            <p className="obd-ai-design-catalog-subpanel__confirm-text">
                                                                                Tree analysis has not been run for this design
                                                                                yet. Run it to explore tree-cutting scenarios.
                                                                            </p>
                                                                        ) : null}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="obd-ai-design-catalog-subpanel__body">
                                                                {designCatalogSubPanel.kind === 'tree' &&
                                                                designCatalogSubPanel.phase === 'confirm' ? (
                                                                    <div className="obd-ai-design-catalog-subpanel__confirm-actions">
                                                                        <Button
                                                                            type="primary"
                                                                            loading={reRunningTreeScenarios}
                                                                            onClick={() => {
                                                                                const p = designCatalogSubPanel;
                                                                                if (
                                                                                    !p ||
                                                                                    p.kind !== 'tree' ||
                                                                                    p.phase !== 'confirm'
                                                                                )
                                                                                    return;
                                                                                handleReRunTreeScenarios(
                                                                                    p.layoutKey,
                                                                                    () => {
                                                                                        setDesignCatalogSubPanel({
                                                                                            kind: 'tree',
                                                                                            phase: 'content',
                                                                                            layoutKey: p.layoutKey,
                                                                                            designLabel: p.designLabel,
                                                                                            mainSnapshotUrl:
                                                                                                p.mainSnapshotUrl,
                                                                                        });
                                                                                        setSelectedRoofCombinationKey(
                                                                                            p.layoutKey,
                                                                                        );
                                                                                        setTreeCuttingForLayoutKey(
                                                                                            p.layoutKey,
                                                                                        );
                                                                                    },
                                                                                );
                                                                            }}
                                                                        >
                                                                            Run tree analysis
                                                                        </Button>
                                                                        <Button onClick={closeDesignCatalogSubPanel}>
                                                                            Cancel
                                                                        </Button>
                                                                    </div>
                                                                ) : null}
                                                                {designCatalogSubPanel.kind === 'tree' &&
                                                                designCatalogSubPanel.phase === 'content' ? (
                                                                    <DashboardAiRailTreeCuttingPanel
                                                                        theme={theme}
                                                                        designLabel={designCatalogSubPanel.designLabel}
                                                                        treeScenariosCatalogViewMode={treeScenariosCatalogViewMode}
                                                                        setTreeScenariosCatalogViewMode={setTreeScenariosCatalogViewMode}
                                                                        treeCuttingForLayoutKey={treeCuttingForLayoutKey}
                                                                        handleReRunTreeScenarios={handleReRunTreeScenarios}
                                                                        reRunningTreeScenarios={reRunningTreeScenarios}
                                                                        sceneViewer={sceneViewer}
                                                                        aiModuleFillingScenarios={aiModuleFillingScenarios}
                                                                        modalTreeData={modalTreeData}
                                                                        layoutMetricsByKey={layoutMetricsByKey}
                                                                        effectiveTreeLayoutKey={effectiveTreeLayoutKey}
                                                                        selectedRoofCombinationKey={
                                                                            selectedRoofCombinationKey
                                                                        }
                                                                        activeLayoutRoofIds={activeLayoutRoofIds}
                                                                        activeModule={activeModule}
                                                                        roofCards={roofCards}
                                                                        renderTreeCutGoodLeapPurchaseOnCard={
                                                                            renderTreeCutGoodLeapPurchaseOnCard
                                                                        }
                                                                        renderTreeCutIgsPrimaryOnCard={
                                                                            renderTreeCutIgsPrimaryOnCard
                                                                        }
                                                                        renderTreeCutLightReachTotalEpcPaymentOnCard={
                                                                            renderTreeCutLightReachTotalEpcPaymentOnCard
                                                                        }
                                                                        solarUtilityData={solarUtilityData}
                                                                        triggerTreeCutScenarioPartnerPricing={
                                                                            triggerTreeCutScenarioPartnerPricing
                                                                        }
                                                                        onOpenTreeScenarioFinancingPricing={
                                                                            handleTreeScenarioFinancingPricingClick
                                                                        }
                                                                        isTreeCutScenarioSaved={isTreeCutScenarioSaved}
                                                                        handleSaveTreeCutScenario={
                                                                            handleSaveTreeCutScenario
                                                                        }
                                                                        applyingScenarioIndex={applyingScenarioIndex}
                                                                        handleUseThisLayout={handleUseThisLayout}
                                                                        treeScenariosByLayoutKey={treeScenariosByLayoutKey}
                                                                        setTreeCuttingPopupOpen={
                                                                            closeTreeCuttingFromCatalogSubpanel
                                                                        }
                                                                        setTreeCuttingForLayoutKey={
                                                                            setTreeCuttingForLayoutKey
                                                                        }
                                                                        setIsDashboardOpen={setIsDashboardOpen}
                                                                        getTreeCutPartnerCellState={getTreeCutPartnerCellState}
                                                                        getTreeCutEpcByPartner={getTreeCutEpcByPartner}
                                                                        isTreeCutEpcLoading={isTreeCutEpcLoading}
                                                                        ensureTreeCutPricingForCard={ensureTreeCutPricingForCard}
                                                                    />
                                                                ) : null}
                                                                {designCatalogSubPanel.kind === 'layouts' && !aiLoading ? (
                                                                    <DashboardAltLayoutOptionsModal
                                                                        theme={theme}
                                                                        embedded
                                                                        open
                                                                        onDismiss={closeDesignCatalogSubPanel}
                                                                        closeModal={closeDesignCatalogSubPanel}
                                                                        altLayoutsCategory={altLayoutsCategory}
                                                                        altLayoutsLoading={altLayoutsLoading}
                                                                        designCountByCategory={designCountByCategory}
                                                                        aiCategoryLayoutVariants={aiCategoryLayoutVariants}
                                                                        globalLayoutVariants={aiModuleFillingLayoutVariants}
                                                                        roofCombinationsFromAi={roofCombinationsFromAi}
                                                                        layoutProductionByKey={layoutProductionByKey}
                                                                        layoutModulesByKey={layoutModulesByKey}
                                                                        layoutMetricsByKey={layoutMetricsByKey}
                                                                        activeModule={activeModule}
                                                                        dashboardSystemSizeKw={dashboardSystemSizeKw}
                                                                        savedAIDesigns={savedAIDesigns}
                                                                        selectedDesignForPricing={selectedDesignForPricing}
                                                                        pricingDesignForTrigger={pricingDesignForTrigger}
                                                                        pricingSelectionLoading={pricingSelectionLoading}
                                                                        aiLayoutVariantSnapshots={aiLayoutVariantSnapshots}
                                                                        layoutSnapshotsByKey={layoutSnapshotsByKey}
                                                                        selectedRoofCombinationKey={selectedRoofCombinationKey}
                                                                        annualUsageCapKwh={annualUsageCapKwh}
                                                                        isLoadingSnapshots={isLoadingSnapshots}
                                                                        handleUseThisLayout={handleUseThisLayout}
                                                                        setIsDashboardOpen={setIsDashboardOpen}
                                                                        sceneViewer={sceneViewer}
                                                                        handleSelectLayoutCard={handleSelectLayoutCard}
                                                                        handlePricingDollarClick={handlePricingDollarClick}
                                                                        onPricingCallClick={props.onPricingCallClick}
                                                                        handleWishlistVariantDesign={
                                                                            handleWishlistVariantDesign
                                                                        }
                                                                        solarUtilityData={solarUtilityData}
                                                                        ensureMainCardPricingForCard={
                                                                            ensureMainCardPricingForCard
                                                                        }
                                                                        getDesignEpcByPartner={getDesignEpcByPartner}
                                                                        getMainCardPartnerCellState={getMainCardPartnerCellState}
                                                                        isMainCardEpcLoading={isMainCardEpcLoading}
                                                                    />
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    ) : aiCatalogViewMode === 'table' ? (
                                                        <DashboardAiLayoutTableView
                                                            theme={theme}
                                                            layoutCategoryNames={layoutCategoryNames}
                                                            layoutCandidatesByCategory={layoutCandidatesByCategory}
                                                            layoutMetricsByKey={layoutMetricsByKey}
                                                            renderMainCardPartnerValue={renderMainCardPartnerValue}
                                                            getSelectedLayoutTitle={getSelectedLayoutTitle}
                                                            totalModulesBestRoofs={totalModulesBestRoofs}
                                                            defaultMaxfillRoofIds={defaultMaxfillRoofIds}
                                                            activeLayoutRoofIds={activeLayoutRoofIds}
                                                            activeLayoutOverUsage={activeLayoutOverUsage}
                                                            simulatingLayouts={simulatingLayouts}
                                                            layoutProductionByKey={layoutProductionByKey}
                                                            selectedRoofCombinationKey={selectedRoofCombinationKey}
                                                            setSelectedRoofCombinationKey={setSelectedRoofCombinationKey}
                                                            activeLayoutProduction={activeLayoutProduction}
                                                            activeModule={activeModule}
                                                            roofCards={roofCards}
                                                            treeCount={treeCount}
                                                            bestRoofsForLayout={bestRoofsForLayout}
                                                            roofCombinationsFromAi={roofCombinationsFromAi}
                                                            layoutModulesByKey={layoutModulesByKey}
                                                            annualUsageCapKwh={annualUsageCapKwh}
                                                            expectedCountByRoofId={expectedCountByRoofId}
                                                            selectedDesignForPricing={selectedDesignForPricing}
                                                            pricingDesignForTrigger={pricingDesignForTrigger}
                                                            pricingSelectionLoading={pricingSelectionLoading}
                                                            savedAIDesigns={savedAIDesigns}
                                                            handleSelectLayoutCard={handleSelectLayoutCard}
                                                            handlePricingDollarClick={handlePricingDollarClick}
                                                            handleApplyLayoutClick={handleApplyLayoutClick}
                                                            handleApplyAdHocLayout={handleApplyAdHocLayout}
                                                            handleWishlistDesign={handleWishlistDesign}
                                                            dashboardSystemSizeKw={dashboardSystemSizeKw}
                                                            treeScenariosByLayoutKey={treeScenariosByLayoutKey}
                                                            setTreeCuttingForLayoutKey={setTreeCuttingForLayoutKey}
                                                            setTreeCuttingPopupOpen={setTreeCuttingPopupOpenWrapped}
                                                            setTreeCuttingConfirmForKey={setTreeCuttingConfirmForKey}
                                                            setTreeCuttingConfirmOpen={setTreeCuttingConfirmOpen}
                                                            layoutSnapshotsByKey={layoutSnapshotsByKey}
                                                            useCatalogInlineSubpanels
                                                            onCatalogTreeSubpanelOpen={handleCatalogTreeSubpanelOpen}
                                                            onOpenCategoryLayoutOptions={handleOpenCategoryLayoutOptions}
                                                            setAltLayoutsCategory={setAltLayoutsCategory}
                                                            setAltLayoutsLoading={setAltLayoutsLoading}
                                                            setAltLayoutsModalOpen={setAltLayoutsModalOpen}
                                                            aiCategoryLayoutVariants={aiCategoryLayoutVariants}
                                                        />
                                                    ) : (
                                                        <div className="editor-ai-layout-card-scroll-body">
                                                            <DashboardAiLayoutCardView
                                                                theme={theme}
                                                                layoutCategoryNames={layoutCategoryNames}
                                                                layoutCandidatesByCategory={layoutCandidatesByCategory}
                                                                layoutProductionByKey={layoutProductionByKey}
                                                                layoutModulesByKey={layoutModulesByKey}
                                                                defaultMaxfillRoofIds={defaultMaxfillRoofIds}
                                                                roofCards={roofCards}
                                                                roofCombinationsFromAi={roofCombinationsFromAi}
                                                                layoutMetricsByKey={layoutMetricsByKey}
                                                                selectedDesignForPricing={selectedDesignForPricing}
                                                                pricingDesignForTrigger={pricingDesignForTrigger}
                                                                simulatingLayouts={simulatingLayouts}
                                                                activeModule={activeModule}
                                                                layoutSnapshotsByKey={layoutSnapshotsByKey}
                                                                isLoadingSnapshots={isLoadingSnapshots}
                                                                annualUsageCapKwh={annualUsageCapKwh}
                                                                getProposalIdForCard={getProposalIdForCard}
                                                                handleApplyLayoutClick={handleApplyLayoutClick}
                                                                handleSelectLayoutCard={handleSelectLayoutCard}
                                                                setSelectedRoofCombinationKey={setSelectedRoofCombinationKey}
                                                                treeScenariosByLayoutKey={treeScenariosByLayoutKey}
                                                                setTreeCuttingForLayoutKey={setTreeCuttingForLayoutKey}
                                                                setTreeCuttingPopupOpen={setTreeCuttingPopupOpenWrapped}
                                                                setAiPanelTab={setAiPanelTab}
                                                                setTreeCuttingConfirmForKey={setTreeCuttingConfirmForKey}
                                                                setTreeCuttingConfirmOpen={setTreeCuttingConfirmOpen}
                                                                sceneViewer={sceneViewer}
                                                                viewerForTreeReRun={sceneViewer}
                                                                aiCategoryLayoutVariants={aiCategoryLayoutVariants}
                                                                selectedRoofCombinationKey={selectedRoofCombinationKey}
                                                                bestRoofsForLayout={bestRoofsForLayout}
                                                                setAltLayoutsCategory={setAltLayoutsCategory}
                                                                setAltLayoutsLoading={setAltLayoutsLoading}
                                                                setAltLayoutsModalOpen={setAltLayoutsModalOpen}
                                                                pricingSelectionLoading={pricingSelectionLoading}
                                                                handlePricingDollarClick={handlePricingDollarClick}
                                                                handleUseThisLayout={handleUseThisLayout}
                                                                setIsDashboardOpen={setIsDashboardOpen}
                                                                treeCount={treeCount}
                                                                savedAIDesigns={savedAIDesigns}
                                                                handleWishlistDesign={handleWishlistDesign}
                                                                detailsModalOpen={detailsModalOpen}
                                                                setDetailsModalOpen={setDetailsModalOpen}
                                                                getSelectedLayoutTitle={getSelectedLayoutTitle}
                                                                totalModulesBestRoofs={totalModulesBestRoofs}
                                                                activeLayoutRoofIds={activeLayoutRoofIds}
                                                                activeLayoutOverUsage={activeLayoutOverUsage}
                                                                activeLayoutProduction={activeLayoutProduction}
                                                                expectedCountByRoofId={expectedCountByRoofId}
                                                                handleReRunTreeScenarios={handleReRunTreeScenarios}
                                                                reRunningTreeScenarios={reRunningTreeScenarios}
                                                                treeScenariosNeedRerun={treeScenariosNeedRerun}
                                                                handleSimulateLayoutsForKeys={handleSimulateLayoutsForKeys}
                                                                mainCardPartnerCellState={mainCardPartnerCellState}
                                                                ensureMainCardPricingForCard={ensureMainCardPricingForCard}
                                                                getDesignEpcByPartner={getDesignEpcByPartner}
                                                                getMainCardPartnerCellState={getMainCardPartnerCellState}
                                                                isMainCardEpcLoading={isMainCardEpcLoading}
                                                                solarUtilityData={solarUtilityData}
                                                                handleApplyAdHocLayout={handleApplyAdHocLayout}
                                                                altLayoutsLoading={altLayoutsLoading}
                                                                altLayoutsCategory={altLayoutsCategory}
                                                                idealLayoutRecommendation={idealLayoutRecommendation}
                                                                setPredictIdealModalOpen={setPredictIdealModalOpen}
                                                                handleMaxPanelsTreeCuttingCardClick={
                                                                    handleMaxPanelsTreeCuttingCardClick
                                                                }
                                                                useCatalogInlineSubpanels
                                                                onCatalogTreeSubpanelOpen={handleCatalogTreeSubpanelOpen}
                                                                onOpenCategoryLayoutOptions={
                                                                    handleOpenCategoryLayoutOptions
                                                                }
                                                                selectedSavedDesignLabel={selectedSavedDesignLabel}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
                                                    <p style={{ color: 'rgba(156,163,175,0.9)', fontSize: 13, margin: 0 }}>
                                                        Run AI to see roof cards with solar access, multiplier, and production.
                                                    </p>
                                                    <Button
                                                        size="small"
                                                        type="default"
                                                        onClick={handleRunAi}
                                                        loading={aiLoading}
                                                        disabled={!runAiReady || aiLoading}
                                                        icon={<LightningIcon size={14} weight="fill" aria-hidden />}
                                                        className="editor-ai-toolbar-btn"
                                                    >
                                                        Run AI Design
                                                    </Button>
                                                    <DashboardRunAiBlockedStatus message={runAiBlockedStatus} />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        )}
                        {resultCardMinimized &&
                            aiPanelTab === 'ai' &&
                            roofCards.length === 0 &&
                            layoutCategoryNames.length === 0 &&
                            aiError == null && (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 12,
                                        alignItems: 'flex-start',
                                        padding: '16px 4px 8px',
                                    }}
                                >
                                    <p
                                        style={{
                                            color: theme === 'dark' ? 'rgba(148,163,184,0.95)' : 'rgba(71,85,105,0.95)',
                                            fontSize: 13,
                                            margin: 0,
                                            lineHeight: 1.45,
                                        }}
                                    >
                                        No AI layout designs yet. Run AI to generate options for this site.
                                    </p>
                                    <Button
                                        type="primary"
                                        size="middle"
                                        onClick={() => void handleRunAi()}
                                        loading={aiLoading}
                                        disabled={!runAiReady || aiLoading}
                                        icon={<LightningIcon size={16} weight="fill" aria-hidden />}
                                        className="editor-ai-toolbar-btn"
                                    >
                                        Run AI Design
                                    </Button>
                                    <DashboardRunAiBlockedStatus message={runAiBlockedStatus} />
                                </div>
                            )}
                        <DashboardPredictIdealLayoutModal
                            theme={theme}
                            open={predictIdealModalOpen}
                            onClose={() => setPredictIdealModalOpen(false)}
                            idealLayoutLoading={idealLayoutLoading}
                            layoutProductionByKey={layoutProductionByKey}
                            handlePredictIdealLayout={handlePredictIdealLayout}
                            idealLayoutRecommendation={idealLayoutRecommendation}
                        />
                        <DashboardGeminiRoofSuggestionsModal
                            theme={theme}
                            open={roofSuggestionsModalOpen}
                            onClose={() => setRoofSuggestionsModalOpen(false)}
                            roofCombinationsFromAi={roofCombinationsFromAi}
                            roofCards={roofCards}
                        />
                        <DashboardAllAiDesignCombinationsModal
                            theme={theme}
                            open={allCombinationsModalOpen}
                            onClose={() => setAllCombinationsModalOpen(false)}
                            roofCombinationsFromAi={roofCombinationsFromAi}
                            roofCards={roofCards}
                            defaultMaxfillRoofIds={defaultMaxfillRoofIds}
                            layoutProductionByKey={layoutProductionByKey}
                            layoutModulesByKey={layoutModulesByKey}
                            layoutMetricsByKey={layoutMetricsByKey}
                            layoutSnapshotsByKey={layoutSnapshotsByKey}
                            simulatingLayouts={simulatingLayouts}
                            activeModule={activeModule}
                            dashboardSystemSizeKw={dashboardSystemSizeKw}
                            annualUsageCapKwh={annualUsageCapKwh}
                            treeCount={treeCount}
                            selectedDesignForPricing={selectedDesignForPricing}
                            pricingDesignForTrigger={pricingDesignForTrigger}
                            pricingSelectionLoading={pricingSelectionLoading}
                            isLoadingSnapshots={isLoadingSnapshots}
                            getProposalIdForCard={getProposalIdForCard}
                            handleApplyLayoutClick={handleApplyLayoutClick}
                            handleSelectLayoutCard={handleSelectLayoutCard}
                            handlePricingDollarClick={handlePricingDollarClick}
                            onPricingCallClick={props.onPricingCallClick}
                            setSelectedRoofCombinationKey={setSelectedRoofCombinationKey}
                            treeScenariosByLayoutKey={treeScenariosByLayoutKey}
                            setTreeCuttingForLayoutKey={setTreeCuttingForLayoutKey}
                            setTreeCuttingPopupOpen={setTreeCuttingPopupOpenWrapped}
                            setAiPanelTab={setAiPanelTab}
                            setTreeCuttingConfirmForKey={setTreeCuttingConfirmForKey}
                            setTreeCuttingConfirmOpen={setTreeCuttingConfirmOpen}
                            sceneViewer={sceneViewer}
                            aiCategoryLayoutVariants={aiCategoryLayoutVariants}
                            selectedRoofCombinationKey={selectedRoofCombinationKey}
                            bestRoofsForLayout={bestRoofsForLayout}
                            layoutCandidatesByCategory={layoutCandidatesByCategory}
                            setAltLayoutsCategory={setAltLayoutsCategory}
                            setAltLayoutsLoading={setAltLayoutsLoading}
                            setAltLayoutsModalOpen={setAltLayoutsModalOpen}
                        />
                        {altLayoutsModalOpen && !designCatalogSubPanel && !aiLoading ? (
                        <DashboardAltLayoutOptionsModal
                            theme={theme}
                            open
                            onDismiss={() => {
                                setAltLayoutsModalOpen(false);
                                setAltLayoutsCategory(null);
                                setAltLayoutsLoading(false);
                            }}
                            closeModal={() => setAltLayoutsModalOpen(false)}
                            altLayoutsCategory={altLayoutsCategory}
                            altLayoutsLoading={altLayoutsLoading}
                            designCountByCategory={designCountByCategory}
                            aiCategoryLayoutVariants={aiCategoryLayoutVariants}
                            globalLayoutVariants={aiModuleFillingLayoutVariants}
                            roofCombinationsFromAi={roofCombinationsFromAi}
                            layoutProductionByKey={layoutProductionByKey}
                            layoutModulesByKey={layoutModulesByKey}
                            layoutMetricsByKey={layoutMetricsByKey}
                            activeModule={activeModule}
                            dashboardSystemSizeKw={dashboardSystemSizeKw}
                            savedAIDesigns={savedAIDesigns}
                            selectedDesignForPricing={selectedDesignForPricing}
                            pricingDesignForTrigger={pricingDesignForTrigger}
                            pricingSelectionLoading={pricingSelectionLoading}
                            aiLayoutVariantSnapshots={aiLayoutVariantSnapshots}
                            layoutSnapshotsByKey={layoutSnapshotsByKey}
                            selectedRoofCombinationKey={selectedRoofCombinationKey}
                            annualUsageCapKwh={annualUsageCapKwh}
                            isLoadingSnapshots={isLoadingSnapshots}
                            handleUseThisLayout={handleUseThisLayout}
                            setIsDashboardOpen={setIsDashboardOpen}
                            sceneViewer={sceneViewer}
                            handleSelectLayoutCard={handleSelectLayoutCard}
                            handlePricingDollarClick={handlePricingDollarClick}
                            onPricingCallClick={props.onPricingCallClick}
                            handleWishlistVariantDesign={handleWishlistVariantDesign}
                            solarUtilityData={solarUtilityData}
                            ensureMainCardPricingForCard={ensureMainCardPricingForCard}
                            getDesignEpcByPartner={getDesignEpcByPartner}
                            getMainCardPartnerCellState={getMainCardPartnerCellState}
                            isMainCardEpcLoading={isMainCardEpcLoading}
                        />
                        ) : null}
                        <DashboardMaxPanelsTreeCuttingModal
                            theme={theme}
                            open={maxPanelsTreeCuttingPopupOpen}
                            onClose={() => setMaxPanelsTreeCuttingPopupOpen(false)}
                            best5MaxPanelsTreeCuttingDesigns={best5MaxPanelsTreeCuttingDesigns}
                            treeCount={treeCount}
                            applyingScenarioIndex={applyingScenarioIndex}
                            handleUseThisLayout={handleUseThisLayout}
                            setIsDashboardOpen={setIsDashboardOpen}
                            selectedRoofCombinationKey={selectedRoofCombinationKey}
                            isTreeCutScenarioSavedForLayout={isTreeCutScenarioSavedForLayout}
                            aiModuleFillingScenarios={aiModuleFillingScenarios}
                            handleSaveTreeCutScenarioWithLayoutKey={handleSaveTreeCutScenarioWithLayoutKey}
                        />
                    </div>
                </>
                )}

                <DashboardSavedDesignsSection
                    aiPanelTab={aiPanelTab}
                    designList={designList}
                    selectedDesignIndex={selectedDesignIndex}
                    sceneViewer={sceneViewer}
                    setIsDashboardOpen={setIsDashboardOpen}
                    navigate={navigate}
                    triggerPricingGridForDesign={triggerPricingGridForDesign}
                    theme={theme}
                    isDashboardOpen={isDashboardOpen}
                    savedDesignsViewMode={savedDesignsViewMode}
                    setSavedDesignsViewMode={setSavedDesignsViewMode}
                />
                {aiPanelTab === 'wishlist' && (
                    <WishListSection
                        setSelectedRoofCombinationKey={setSelectedRoofCombinationKey}
                        handleApplyLayoutClick={handleApplyLayoutClick}
                        handleUseThisLayout={handleUseThisLayout}
                        handlePricingForSavedDesign={handlePricingForSavedDesign}
                        selectedDesignForPricing={selectedDesignForPricing}
                        pricingDesignForTrigger={pricingDesignForTrigger}
                        pricingSelectionLoading={pricingSelectionLoading}
                        solarUtilityData={solarUtilityData}
                        ensureMainCardPricingForCard={ensureMainCardPricingForCard}
                        getDesignEpcByPartner={getDesignEpcByPartner}
                        getMainCardPartnerCellState={getMainCardPartnerCellState}
                        isMainCardEpcLoading={isMainCardEpcLoading}
                        catalogViewMode={aiCatalogViewMode}
                        setCatalogViewMode={setAiCatalogViewMode}
                        hideHeaderChrome={!isDashboardOpen}
                    />
                )}
                    </>
    );

    const slideRailContent = (
        <>
            {aiPanelTab === 'wishlist' ? (
                <DashboardAiSlideWishlistList
                    theme={theme}
                    setSelectedRoofCombinationKey={setSelectedRoofCombinationKey}
                    handleApplyLayoutClick={handleApplyLayoutClick}
                    handleUseThisLayout={handleUseThisLayout}
                    setIsDashboardOpen={setIsDashboardOpen}
                    selectedDesignForPricing={selectedDesignForPricing}
                    pricingDesignForTrigger={pricingDesignForTrigger}
                    pricingSelectionLoading={pricingSelectionLoading}
                    handlePricingDollarClick={handlePricingDollarClick}
                    solarUtilityData={solarUtilityData}
                    ensureMainCardPricingForCard={ensureMainCardPricingForCard}
                    getDesignEpcByPartner={getDesignEpcByPartner}
                    getMainCardPartnerCellState={getMainCardPartnerCellState}
                    isMainCardEpcLoading={isMainCardEpcLoading}
                />
            ) : null}

            {aiPanelTab === 'ai' && !hasAiLayoutCatalog && (
                <DashboardAiRailAiEmptyState
                    handleRunAi={handleRunAi}
                    aiLoading={aiLoading}
                    runAiReady={runAiReady}
                    runAiBlockedStatus={runAiBlockedStatus}
                />
            )}

            {aiPanelTab === 'ai' && hasAiLayoutCatalog && (
                <>
                    <DashboardAiSlideDesignsList
                        theme={theme}
                        layoutCategoryNames={layoutCategoryNames}
                        layoutCandidatesByCategory={layoutCandidatesByCategory}
                        layoutProductionByKey={layoutProductionByKey}
                        layoutModulesByKey={layoutModulesByKey}
                        defaultMaxfillRoofIds={defaultMaxfillRoofIds}
                        roofCards={roofCards}
                        roofCombinationsFromAi={roofCombinationsFromAi}
                        layoutMetricsByKey={layoutMetricsByKey}
                        layoutSnapshotsByKey={layoutSnapshotsByKey}
                        aiCategoryLayoutVariants={aiCategoryLayoutVariants}
                        isLoadingSnapshots={isLoadingSnapshots}
                        selectedDesignForPricing={selectedDesignForPricing}
                        pricingDesignForTrigger={pricingDesignForTrigger}
                        simulatingLayouts={simulatingLayouts}
                        activeModule={activeModule}
                        annualUsageCapKwh={annualUsageCapKwh}
                        treeCount={treeCount}
                        handleSelectLayoutCard={handleSelectLayoutCard}
                        handleApplyLayoutClick={handleApplyLayoutClick}
                        pricingSelectionLoading={pricingSelectionLoading}
                        handlePricingDollarClick={handlePricingDollarClick}
                        onReRunAiDesigns={() => void handleRunAi()}
                        reRunAiLoading={aiLoading}
                        reRunAiDisabled={!runAiReady || aiLoading}
                        selectedSavedDesignLabel={selectedSavedDesignLabel}
                        aiDesignsDataSource={aiDesignsDataSource}
                    />
                </>
            )}
        </>
    );

    if (!hasCapability('Show_OneButton') && !hasCapability('Show_AI_Scenarios')) return null;

    const treeCuttingScenariosModalEl = (
        <DashboardModal
            title={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <TreeEvergreenIcon size={20} weight="duotone" aria-hidden />
                    Tree cutting scenarios
                </span>
            }
            open={isTreeCuttingScenariosModalVisible}
            onClose={closeTreeCutScenariosPopup}
            theme={theme === 'light' ? 'light' : 'dark'}
            footer={null}
            width={Math.min(1360, typeof window !== 'undefined' ? window.innerWidth - 32 : 1360)}
            zIndex={OBD_EDITOR_TREE_CUTTING_MODAL_Z}
            getContainer={() => document.body}
            destroyOnClose
            className={`editor-ai-tree-cutting-scenarios-modal obd-theme-${theme}`}
            rootClassName={`editor-ai-tree-cutting-scenarios-modal-root editor-ai-tree-cutting-scenarios-modal-root--viewer obd-form-controls-modal-root obd-theme-${theme}`}
            modalStyles={{ body: { maxHeight: 'min(85vh, 880px)', overflow: 'hidden' } }}
        >
            <DashboardAiRailTreeCuttingPanel
                theme={theme}
                treeScenariosCatalogViewMode={treeScenariosCatalogViewMode}
                setTreeScenariosCatalogViewMode={setTreeScenariosCatalogViewMode}
                treeCuttingForLayoutKey={treeCuttingForLayoutKey}
                handleReRunTreeScenarios={handleReRunTreeScenarios}
                reRunningTreeScenarios={reRunningTreeScenarios}
                sceneViewer={sceneViewer}
                aiModuleFillingScenarios={aiModuleFillingScenarios}
                modalTreeData={modalTreeData}
                layoutMetricsByKey={layoutMetricsByKey}
                effectiveTreeLayoutKey={effectiveTreeLayoutKey}
                selectedRoofCombinationKey={selectedRoofCombinationKey}
                activeLayoutRoofIds={activeLayoutRoofIds}
                activeModule={activeModule}
                roofCards={roofCards}
                renderTreeCutGoodLeapPurchaseOnCard={renderTreeCutGoodLeapPurchaseOnCard}
                renderTreeCutIgsPrimaryOnCard={renderTreeCutIgsPrimaryOnCard}
                renderTreeCutLightReachTotalEpcPaymentOnCard={renderTreeCutLightReachTotalEpcPaymentOnCard}
                solarUtilityData={solarUtilityData}
                triggerTreeCutScenarioPartnerPricing={triggerTreeCutScenarioPartnerPricing}
                onOpenTreeScenarioFinancingPricing={handleTreeScenarioFinancingPricingClick}
                isTreeCutScenarioSaved={isTreeCutScenarioSaved}
                handleSaveTreeCutScenario={handleSaveTreeCutScenario}
                applyingScenarioIndex={applyingScenarioIndex}
                handleUseThisLayout={handleUseThisLayout}
                treeScenariosByLayoutKey={treeScenariosByLayoutKey}
                setTreeCuttingPopupOpen={setTreeCuttingPopupOpenWrapped}
                setTreeCuttingForLayoutKey={setTreeCuttingForLayoutKey}
                setIsDashboardOpen={setIsDashboardOpen}
                getTreeCutPartnerCellState={getTreeCutPartnerCellState}
                getTreeCutEpcByPartner={getTreeCutEpcByPartner}
                isTreeCutEpcLoading={isTreeCutEpcLoading}
                ensureTreeCutPricingForCard={ensureTreeCutPricingForCard}
                supplementsBelowThumb
            />
        </DashboardModal>
    );

    if (!hasCapability('Show_OneButton')) {
        return (
            <>
                <DashboardAiRailSection
                    aiRailOpen={aiRailOpen}
                    setAiRailOpen={setAiRailOpen}
                    setTreeCuttingPopupOpen={setTreeCuttingPopupOpenWrapped}
                    setTreeCuttingForLayoutKey={setTreeCuttingForLayoutKey}
                    isDashboardOpen={false}
                    theme={theme}
                    aiPanelTab={aiPanelTab}
                    setAiPanelTab={setAiPanelTab}
                    aiLoading={aiLoading}
                >
                    {slideRailContent}
                </DashboardAiRailSection>
                {treeCuttingScenariosModalEl}
            </>
        );
    }

    return (
        <>
        <DashboardOverlayShell 
            isDashboardOpen={isDashboardOpen} 
            overlayVisible={overlayVisible}>
            <OpportunityInitializationOverlay
                theme={theme}
                visible={overlayVisible}
                onSwitchToViewer={onView3dPeekClick}
                onRetry={handleRetryOpportunityInitialization}
            />
            <DashboardSurfaceSection
                theme={theme}
                initialDashboardReady={initialDashboardReady}
                isDashboardOpen={isDashboardOpen}
                layoutVariant={dashboardLayoutVariant}
            >
                {/* Opportunity Information ready gate. oneDRAW draw + shade stay deferred
                    until the user opens the 3D viewer. */}
                <DashboardLoadingGate
                    initialDashboardReady={initialDashboardReady && isDashboardOpen}
                    showContentUnderOverlay={overlayVisible && isOpportunityInitializing}
                    siteLoadFailed={siteViewerUnavailable}
                >
                <DashboardReadyLayoutSection
                    theme={theme}
                    siteAddress={siteAddress}
                    titleSearchData={titleSearchData}
                    oppRefreshKey={oppRefreshKey}
                    setAddTitleContactsModalOpen={setAddTitleContactsModalOpen}
                    handleOpenBlackKnightLeadModal={handleOpenBlackKnightLeadModal}
                    dashboardAnnualUsage={dashboardAnnualUsage}
                    dashboardSystemSizeKw={dashboardSystemSizeKw}
                    dashboardAvgDailyUsage={dashboardAvgDailyUsage}
                    dashboardMultiplier={dashboardMultiplier}
                    triggerPricingGridForDesign={triggerPricingGridForDesign}
                    priceQuote={priceQuote}
                    isDashboardOpen={isDashboardOpen}
                    setIsDashboardOpen={setIsDashboardOpen}
                    pricingFetchTrigger={pricingFetchTrigger}
                    pricingDesignForTrigger={pricingDesignForTrigger}
                    setPricingFetchTrigger={setPricingFetchTrigger}
                    opportunityDetails={opportunityDetails}
                    selectedDesignForPricing={selectedDesignForPricing}
                    setPricingGridModalOpen={setPricingGridModalOpen}
                    opportunityID={opportunityID}
                    financePartnerSyncModalOpen={financePartnerSyncModalOpen}
                    setFinancePartnerSyncModalOpen={setFinancePartnerSyncModalOpen}
                    leadSyncFormOpen={leadSyncFormOpen}
                    setLeadSyncFormOpen={setLeadSyncFormOpen}
                    initialDashboardReady={initialDashboardReady}
                    dashboardSectionPanel={dashboardSectionPanel}
                    setDashboardSectionPanel={setDashboardSectionPanel}
                    designsDashboardSlot={
                        dashboardSectionPanel === 'designs' ? (
                            <div className={`obd-dashboard-inline-designs obd-theme-${theme}`}>
                                <div className="obd-dashboard-inline-designs__chrome-wrap obd-ai-rail__chrome obd-ai-rail__chrome--inline">
                                    <DashboardAiDesignsTabChrome aiPanelTab={aiPanelTab} setAiPanelTab={setAiPanelTab} />
                                </div>
                                <div className="obd-ai-rail__body obd-dashboard-inline-designs__body">{aiRailMainContent}</div>
                            </div>
                        ) : null
                    }
                    ahjTown={ahjTown}
                    setbackNotes={setbackNotes}
                    setAhjModalOpen={setAhjModalOpen}
                    setOppValidationModalOpen={setOppValidationModalOpen}
                    aiLoading={aiLoading}
                />
                </DashboardLoadingGate>
            </DashboardSurfaceSection>
            <DashboardAiRailSection
                aiRailOpen={aiRailOpen}
                setAiRailOpen={setAiRailOpen}
                setTreeCuttingPopupOpen={setTreeCuttingPopupOpenWrapped}
                setTreeCuttingForLayoutKey={setTreeCuttingForLayoutKey}
                isDashboardOpen={isDashboardOpen}
                theme={theme}
                aiPanelTab={aiPanelTab}
                setAiPanelTab={setAiPanelTab}
                aiLoading={aiLoading}
            >
                {slideRailContent}
            </DashboardAiRailSection>
            <DashboardOverlayExtrasSection
                initialDashboardReady={initialDashboardReady}
                isDashboardOpen={isDashboardOpen}
                onOpenAssistant={() => setOpportunityAssistantOpen(true)}
                opportunityAssistantOpen={opportunityAssistantOpen}
                setOpportunityAssistantOpen={setOpportunityAssistantOpen}
                pricingGridModalOpen={pricingGridModalOpen}
                setPricingGridModalOpen={setPricingGridModalOpen}
                theme={theme}
                pricingFetchTrigger={pricingFetchTrigger}
                pricingDesignForTrigger={pricingDesignForTrigger}
                priceQuote={priceQuote}
                setPricingFetchTrigger={setPricingFetchTrigger}
                ahjModalOpen={ahjModalOpen}
                setAhjModalOpen={setAhjModalOpen}
                ahjTown={ahjTown}
                setbackNotes={setbackNotes}
                addTitleContactsModalOpen={addTitleContactsModalOpen}
                setAddTitleContactsModalOpen={setAddTitleContactsModalOpen}
                titleSearchData={titleSearchData}
                handleTitleSyncSuccess={handleTitleSyncSuccess}
                blackKnightModalOpen={blackKnightModalOpen}
                setBlackKnightModalOpen={setBlackKnightModalOpen}
                blackKnightModalData={blackKnightModalData}
                opportunityID={opportunityID}
                oppValidationModalOpen={oppValidationModalOpen}
                setOppValidationModalOpen={setOppValidationModalOpen}
                activeAIDesignId={activeAIDesignId}
                activeSubScenarioId={activeSubScenarioId}
                savedAIDesigns={savedAIDesigns}
                pricingGridElevatedStack={isTreeCuttingScenariosModalVisible}
                dashboardSectionPanel={dashboardSectionPanel}
                salesForceId={salesForceId}
                commerceHubLoading={commerceHubLoading}
                onSolarPricingClick={onCommerceHubSolarPricingClick}
                opportunityNumericId={opportunityNumericId}
            />

            <DashboardTreeCuttingConfirmModal
                theme={theme}
                open={treeCuttingConfirmOpen && !designCatalogSubPanel}
                onCancel={() => {
                    setTreeCuttingConfirmOpen(false);
                    setTreeCuttingConfirmForKey(null);
                }}
                onConfirm={() => {
                    const key = treeCuttingConfirmForKey ?? selectedRoofCombinationKey;
                    setTreeCuttingConfirmOpen(false);
                    setTreeCuttingConfirmForKey(null);
                    if (key != null) {
                        handleReRunTreeScenarios(key, () => {
                            setTreeCuttingForLayoutKey(key);
                            openTreeCutScenariosModal();
                        });
                    }
                }}
                sceneViewer={sceneViewer}
                reRunningTreeScenarios={reRunningTreeScenarios}
            />

        </DashboardOverlayShell>
        {treeCuttingScenariosModalEl}
        {initialDashboardReady && isDashboardOpen && savingScenarios ? (
            <div className="editor-ai-saving-toast-mount editor-ai-saving-toast-mount--dashboard">
                <DashboardSavingProgressToast
                    theme={theme}
                    visible={savingScenarios}
                    progress={aiSavingProgress}
                />
            </div>
        ) : null}
        {initialDashboardReady && isDashboardOpen ? <DashboardAgentFooter /> : null}
        {initialDashboardReady ? (
            <>
                <SiteViewerUnavailableModal theme={theme} />
                <DashboardAiRailPeekButton
                    aiRailOpen={aiRailOpen}
                    isDashboardOpen={isDashboardOpen}
                    theme={theme}
                    hoverAvailable={hoverJobAvailable}
                    hoverJobId={hoverJobId}
                    onView3d={onView3dPeekClick}
                />
            </>
        ) : null}
        </>
    );
};